import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { apiMasterAPI, cleaningConfigAPI, detergentMasterAPI, equipmentAPI, productsAPI, settingsAPI, aiAPI, riskAssessmentAPI, macoAPI } from '../services/api';
import { withReportProfile, unitReportAddress } from '../utils/reportProfiles';
import { calculateMacoValues, toMg } from './macoCalculator';
import { FaAngleDown, FaDownload, FaCog, FaExclamationTriangle, FaTable, FaIdCard, FaListAlt, FaPrint, FaFlask, FaCheckSquare, FaCogs, FaRobot, FaCapsules, FaLayerGroup, FaFileUpload } from 'react-icons/fa';
import { StatCard } from '../components/UnifiedUIComponents';
import { openPrintPreview } from '../utils/printUtils';
import FloatingScrollWrapper from '../components/FloatingScrollWrapper';
import AIResultPanel from '../components/AIResultPanel';
import { Modal, ModalFooter, ESignModal } from '../components/Modals';
import useAIAction from '../hooks/useAIAction';
import TablePagination from '../components/TablePagination';
const MACO_HBEL_SF_SETTING_KEY = 'maco_hbel_safety_factor';
const MACO_DOSE_SF_SETTING_KEY = 'maco_dose_safety_factor';
const MACO_10PPM_SETTING_KEY = 'maco_ppm_limit';
const MACO_TOX_SF_SETTING_KEY = 'maco_toxicological_safety_factor';
const MACO_CRITERIA_ENABLED_KEY = 'maco_criteria_enabled';
const EQUIPMENT_SURFACE_AREA_KEY = 'equipment_surface_area_cm2';
const MACO_DETERGENT_LD50_KEY = 'maco_detergent_ld50';
const MACO_DETERGENT_BW_KEY = 'maco_detergent_bw';
const MACO_DETERGENT_SF_KEY = 'maco_detergent_sf';
const MACO_PURGING_FACTOR_KEY = 'maco_purging_factor';
const MACO_SF_KEY = 'maco_safety_factor_sf';
const MACO_SWAB_RECOVERY_KEY = 'maco_swab_recovery_pct';
const MACO_RINSATE_VOLUME_KEY = 'maco_rinsate_volume_ml';
const MACO_SWAB_AREA_KEY = 'maco_swab_area_cm2';
const TRAIN_AREA_COMPENSATION_KEY = 'train_area_compensation_percent';
const MACO_DOSE_SF_BY_ROUTE_KEY = 'maco_dose_sf_by_route';

const DEFAULT_DOSE_SF_BY_ROUTE = {
  oral: 1000,
  topical: 1000,
  injectable: 100,
  inhalation: 100,
  ophthalmic: 100,
  transdermal: 1000,
};

const DEFAULT_CRITERIA_ENABLED = { hbel: true, dose: true, ppm: true, tox: true, detergent: false };

const toNum = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const fmt = (value, digits = 6) => toNum(value, 0).toFixed(digits);

const CriteriaCard = ({ title, formula, safetyFactor, macoValue, marValue, isGoverning, enabled, macoUnitLabel = 'mg', marUnitLabel = 'µg/cm²', displayMacoVal, displayMarVal, notUsedMessage }) => {
  const showMaco = displayMacoVal ? displayMacoVal(macoValue) : macoValue;
  const showMar  = displayMarVal  ? displayMarVal(marValue)   : marValue;
  return (
    <div style={{
      border: isGoverning ? '3px solid var(--success-color, #10b981)' : '1px solid var(--border-color, #e5e7eb)',
      borderRadius: '12px',
      padding: '16px',
      backgroundColor: isGoverning ? 'var(--success-bg, #f0fdf4)' : 'var(--surface, #fff)',
      boxShadow: isGoverning ? '0 4px 12px rgba(16, 185, 129, 0.15)' : '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '12px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: '600', color: 'var(--text-primary, #111827)' }}>
          {title}
          {isGoverning && <span style={{ marginLeft: '8px', color: 'var(--success-color, #10b981)' }}>★ GOVERNING</span>}
        </h3>
        <span style={{
          padding: '4px 8px',
          backgroundColor: enabled ? 'var(--info-bg, #dbeafe)' : 'var(--surface-light, #f3f4f6)',
          color: enabled ? 'var(--info-color, #0369a1)' : 'var(--text-secondary, #6b7280)',
          borderRadius: '4px',
          fontSize: 'var(--fs-md)',
          fontWeight: '500'
        }}>
          {enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>

      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary, #6b7280)', marginBottom: '8px', fontFamily: 'monospace', backgroundColor: 'var(--surface-light, #f9fafb)', padding: '8px', borderRadius: '4px' }}>
        {formula}
      </div>

      {notUsedMessage ? (
        <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary, #6b7280)', padding: '8px 2px' }}>{notUsedMessage}</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary, #6b7280)', marginBottom: '2px' }}>Safety Factor</div>
              <div style={{ fontSize: 'var(--fs-xl)', fontWeight: '700', color: 'var(--text-primary, #111827)' }}>{fmt(safetyFactor, 4)}</div>
            </div>
            <div>
              <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary, #6b7280)', marginBottom: '2px' }}>MACO ({macoUnitLabel})</div>
              <div style={{ fontSize: 'var(--fs-xl)', fontWeight: '700', color: 'var(--primary-color, #2563eb)' }}>{showMaco !== null ? showMaco.toFixed(6) : '—'}</div>
            </div>
          </div>

          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
            <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary, #6b7280)', marginBottom: '2px' }}>MAR/cm² ({marUnitLabel})</div>
            <div style={{ fontSize: 'var(--fs-xl)', fontWeight: '700', color: 'var(--success-color, #059669)' }}>{showMar !== null ? showMar.toFixed(6) : '—'}</div>
          </div>
        </>
      )}
    </div>
  );
};

const MACO = () => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [apis, setApis] = useState([]);
  const [trains, setTrains] = useState([]);
  const [selectedTrainId, setSelectedTrainId] = useState('');
  const [previousApiId, setPreviousApiId] = useState('');
  const [nextProductId, setNextProductId] = useState('');
  const [nextApiId, setNextApiId] = useState('');
  const [trainMembers, setTrainMembers] = useState([]);
  const [nextProductRisk, setNextProductRisk] = useState(null);
  const [serverMacoMatrix, setServerMacoMatrix] = useState(null);
  const [currentGoverning, setCurrentGoverning] = useState(null);
  const [showGoverningESign, setShowGoverningESign] = useState(false);
  const [governingESignData, setGoverningESignData] = useState({ password: '', comment: '' });
  const [finalizingGoverning, setFinalizingGoverning] = useState(false);
  const [governingWcPair, setGoverningWcPair] = useState(null);
  const governingPasswordRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('matrix');
  const [leastTip, setLeastTip] = useState(null); // position of the hovered red (least) matrix cell
  const [navHighlightRoute, setNavHighlightRoute] = useState('');
  const routeHighlightRef = useRef(null);
  const [matrixCriterion, setMatrixCriterion] = useState('governing');
  const [matrixUnit, setMatrixUnit] = useState('maco');
  const [matrixMode, setMatrixMode] = useState('api');  // 'product' or 'api'

  const [hbelSafetyFactor, setHbelSafetyFactor] = useState(1);
  const [doseSafetyFactor, setDoseSafetyFactor] = useState(1000);
  const [ppmLimit, setPpmLimit] = useState(10);
  const [toxSafetyFactor, setToxSafetyFactor] = useState(1000);
  const [criteriaEnabled, setCriteriaEnabled] = useState(DEFAULT_CRITERIA_ENABLED);
  const [surfaceAreaCm2, setSurfaceAreaCm2] = useState(4932);
  const [showSettings, setShowSettings] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfSections, setPdfSections] = useState({ summary: true, governing: true, hbel: true, dose: true, ppm: true, tox: true, detergent: true });

  // Detergent criterion states (read from settings)
  const [detergentLd50, setDetergentLd50] = useState(0);
  const [detergentBodyWeight, setDetergentBodyWeight] = useState(70);
  const [detergentSafetyFactor, setDetergentSafetyFactor] = useState(2000);

  // Detergent name(s) sourced from Cleaning Config page for the active plant/line scope
  const [configDetergentName, setConfigDetergentName] = useState('');
  const [configDetergentNames, setConfigDetergentNames] = useState([]);
  // Whether ANY equipment in the current plant/line scope is actually cleaned with a
  // detergent (per Cleaning Config's detergent_used flag) — used to hide the Detergent
  // criterion from Settings entirely when it genuinely doesn't apply to this scope,
  // rather than just showing it as an unused toggle.
  const [detergentUsedInScope, setDetergentUsedInScope] = useState(false);

  // Detergent master list
  const [detergentList, setDetergentList] = useState([]);
  const [detModalOpen, setDetModalOpen]   = useState(false);
  const [detModalForm, setDetModalForm]   = useState({ id: null, detergent_name: '', category: '', concentration: '', notes: '' });
  const [detSaving,    setDetSaving]      = useState(false);
  const [detImportPreview, setDetImportPreview] = useState(null); // { fileName, rows }
  const [detImportResult, setDetImportResult] = useState(null);
  const [detImporting, setDetImporting] = useState(false);
  const detImportInputRef = useRef(null);

  const [purgingFactor, setPurgingFactor] = useState(1.0);
  const [macoSF, setMacoSF] = useState(1);

  // Local line filter (overrides header; 'All' = all trains for the plant)
  const [macoLineFilter, setMacoLineFilter] = useState('');

  // Per-train contact surface area (derived from train members; null = fall back to global setting)
  const [trainSurfaceAreaCm2, setTrainSurfaceAreaCm2] = useState(null);
  const [trainSurfaceLoading, setTrainSurfaceLoading] = useState(false);

  // Unit display toggles
  const [macoDisplayUnit, setMacoDisplayUnit] = useState('mg');      // 'mg' | 'mcg'
  const [marDisplayUnit,  setMarDisplayUnit]  = useState('mcg/cm2'); // 'mcg/cm2' | 'mg/cm2'
  const [doseSFByRoute, setDoseSFByRoute] = useState(DEFAULT_DOSE_SF_BY_ROUTE);
  const [swabRecoveryPct, setSwabRecoveryPct] = useState(70);
  const [rinsateVolumeMl, setRinsateVolumeMl] = useState(5000);
  const [swabAreaCm2, setSwabAreaCm2] = useState(25);
  const [trainAreaCompensationPct, setTrainAreaCompensationPct] = useState(10); // % added to nominal surface area for inaccessible zones

  const [reportSettings, setReportSettings] = useState({});
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const userRole = (() => {
    try { return String(JSON.parse(localStorage.getItem('user') || '{}')?.role || '').trim().toLowerCase(); }
    catch { return ''; }
  })();
  const canEditSettings = ['admin', 'super admin', 'qa'].includes(userRole);

  const activeLineRoute = localStorage.getItem('activeLineRoute') || '';
  const activeLineDosageLabel = localStorage.getItem('activeLineDosageLabel') || '';
  const ROUTE_DISPLAY = { oral: 'Oral / Solid', topical: 'Topical / Dermal', injectable: 'Injectable / Parenteral', inhalation: 'Inhalation', ophthalmic: 'Ophthalmic', transdermal: 'Transdermal' };

  const applySettingsState = (settings) => {
    setHbelSafetyFactor(Math.max(0.000001, toNum(settings[MACO_HBEL_SF_SETTING_KEY], 1)));
    setDoseSafetyFactor(Math.max(0.000001, toNum(settings[MACO_DOSE_SF_SETTING_KEY], 1000)));
    setPpmLimit(Math.max(0, toNum(settings[MACO_10PPM_SETTING_KEY], 10)));
    setToxSafetyFactor(Math.max(0.000001, toNum(settings[MACO_TOX_SF_SETTING_KEY], 1000)));
    setSurfaceAreaCm2(Math.max(1, toNum(settings[EQUIPMENT_SURFACE_AREA_KEY], 4932)));
    try {
      const parsed = JSON.parse(settings[MACO_CRITERIA_ENABLED_KEY] || "{}");
      setCriteriaEnabled({ ...DEFAULT_CRITERIA_ENABLED, ...parsed });
    } catch {
      setCriteriaEnabled(DEFAULT_CRITERIA_ENABLED);
    }
    setDetergentLd50(toNum(settings[MACO_DETERGENT_LD50_KEY], 0));
    setDetergentBodyWeight(Math.max(0.1, toNum(settings[MACO_DETERGENT_BW_KEY], 70)));
    setDetergentSafetyFactor(Math.max(1, toNum(settings[MACO_DETERGENT_SF_KEY], 2000)));
    setPurgingFactor(Math.max(1, toNum(settings[MACO_PURGING_FACTOR_KEY], 1.0)));
    setMacoSF(Math.max(0.01, toNum(settings[MACO_SF_KEY], 1)));
    try {
      const parsedRoutes = JSON.parse(settings[MACO_DOSE_SF_BY_ROUTE_KEY] || '{}');
      setDoseSFByRoute({ ...DEFAULT_DOSE_SF_BY_ROUTE, ...parsedRoutes });
    } catch { setDoseSFByRoute(DEFAULT_DOSE_SF_BY_ROUTE); }
    setSwabRecoveryPct(Math.min(100, Math.max(1, toNum(settings[MACO_SWAB_RECOVERY_KEY], 70))));
    setRinsateVolumeMl(Math.max(1, toNum(settings[MACO_RINSATE_VOLUME_KEY], 5000)));
    setSwabAreaCm2(Math.max(1, toNum(settings[MACO_SWAB_AREA_KEY], 25)));
    setTrainAreaCompensationPct(Math.max(0, toNum(settings[TRAIN_AREA_COMPENSATION_KEY], 10)));
  };

  const refreshSettings = async () => {
    try {
      const settingsRes = await settingsAPI.getAll();
      const settings = settingsRes?.data?.data || {};
      setReportSettings(settings);
      applySettingsState(settings);
    } catch {
      // Non-fatal — local values from the save just performed remain in place.
    }
  };

  const saveSettings = async () => {
    if (!canEditSettings) return;
    try {
      setSettingsSaving(true);
      await Promise.all([
        settingsAPI.updateSetting(MACO_HBEL_SF_SETTING_KEY, String(Math.max(0.000001, hbelSafetyFactor))),
        settingsAPI.updateSetting(MACO_DOSE_SF_SETTING_KEY, String(Math.max(0.000001, doseSafetyFactor))),
        settingsAPI.updateSetting(MACO_10PPM_SETTING_KEY, String(Math.max(0, ppmLimit))),
        settingsAPI.updateSetting(MACO_TOX_SF_SETTING_KEY, String(Math.max(0.000001, toxSafetyFactor))),
        settingsAPI.updateSetting(MACO_CRITERIA_ENABLED_KEY, JSON.stringify(criteriaEnabled)),
        settingsAPI.updateSetting(MACO_DETERGENT_LD50_KEY, String(Math.max(0, detergentLd50))),
        settingsAPI.updateSetting(MACO_DETERGENT_BW_KEY, String(Math.max(0.1, detergentBodyWeight))),
        settingsAPI.updateSetting(MACO_DETERGENT_SF_KEY, String(Math.max(1, detergentSafetyFactor))),
        settingsAPI.updateSetting(MACO_PURGING_FACTOR_KEY, String(Math.max(1, purgingFactor))),
        settingsAPI.updateSetting(MACO_SF_KEY, String(Math.max(0.01, macoSF))),
        settingsAPI.updateSetting(MACO_DOSE_SF_BY_ROUTE_KEY, JSON.stringify(doseSFByRoute)),
        settingsAPI.updateSetting(MACO_SWAB_RECOVERY_KEY, String(Math.min(100, Math.max(1, swabRecoveryPct)))),
        settingsAPI.updateSetting(MACO_RINSATE_VOLUME_KEY, String(Math.max(1, rinsateVolumeMl))),
        settingsAPI.updateSetting(MACO_SWAB_AREA_KEY, String(Math.max(1, swabAreaCm2))),
        settingsAPI.updateSetting(TRAIN_AREA_COMPENSATION_KEY, String(Math.max(0, trainAreaCompensationPct))),
      ]);
      setSettingsDirty(false);
      toast.success('Settings saved successfully');
      await refreshSettings();
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const canCalculate = useMemo(() => {
    return selectedTrainId && previousApiId && nextProductId;
  }, [selectedTrainId, previousApiId, nextProductId]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const activeLine = localStorage.getItem('activeLine');
        const [productsRes, apisRes, trainsRes, settingsRes] = await Promise.all([
          productsAPI.getAll({ limit: 10000 }),
          apiMasterAPI.getAll({ limit: 10000 }),
          equipmentAPI.getTrains(),
          settingsAPI.getAll()
        ]);

        const extractRows = (payload) => {
          if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
          if (Array.isArray(payload?.data?.data)) return payload.data.data;
          if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
          if (Array.isArray(payload?.data)) return payload.data;
          return [];
        };

        let productRows = extractRows(productsRes);
        const apiRows = extractRows(apisRes);
        let trainRows = extractRows(trainsRes);
        const settings = settingsRes?.data?.data || {};
        const activePlant = localStorage.getItem('activePlant');

        // Equipment/train/product endpoints already apply the shared selected-unit scope.
        // Do NOT filter trains by activeLine here — the MACO page has its own local line filter
        // so users can switch lines without changing the global header selection.

        setProducts(productRows);
        setApis(apiRows);
        setTrains(trainRows);
        // Initialise local line filter to the header's active line (can be changed within MACO)
        setMacoLineFilter(activeLine && activeLine !== 'All' ? activeLine : 'All');
        setReportSettings(settings);
        applySettingsState(settings);

        // Load detergent info from Cleaning Config for active line
        try {
          const ccParams = {};
          if (activeLine && activeLine !== 'All') ccParams.line_id = activeLine;
          if (activePlant && activePlant !== 'All') ccParams.plant_code = activePlant;
          const [ccRes, detRes] = await Promise.all([
            cleaningConfigAPI.getAll(ccParams),
            detergentMasterAPI.getAll(),
          ]);
          const ccRows = ccRes?.data?.data || [];
          setDetergentList(detRes?.data?.data || []);
          const withDetergent = ccRows.filter(r => r.detergent_used && r.detergent_name);
          setDetergentUsedInScope(withDetergent.length > 0);
          if (withDetergent.length > 0) {
            const names = [...new Set(withDetergent.map(r => r.detergent_name))];
            setConfigDetergentName(names.join(', '));
            setConfigDetergentNames(names);
            setCriteriaEnabled(prev => ({ ...prev, detergent: true }));
          }
        } catch {
          // Non-fatal
        }

        if (trainRows.length) {
          setSelectedTrainId(String(trainRows[0].train_id));
        }
        if (apiRows.length) {
          setPreviousApiId(String(apiRows[0].api_id));
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load MACO data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const selectedTrain = useMemo(
    () => trains.find((row) => String(row.train_id) === String(selectedTrainId)) || null,
    [trains, selectedTrainId]
  );

  useEffect(() => {
    if (!selectedTrainId) { setServerMacoMatrix(null); return; }
    let cancelled = false;
    setServerMacoMatrix(null);
    macoAPI.getMatrix(selectedTrainId).then((res) => {
      if (!cancelled) setServerMacoMatrix(res?.data?.data || null);
    }).catch(() => { if (!cancelled) setServerMacoMatrix(null); });
    return () => { cancelled = true; };
  }, [selectedTrainId]);

  // Default the on-screen pair selector to the train's actual worst case (the
  // lowest-MAR pair from the server matrix) rather than an arbitrary first
  // API/product — once per train selection, so a deliberate manual change
  // afterward is never overwritten.
  const autoDefaultedTrainRef = useRef(null);
  useEffect(() => {
    if (!selectedTrainId || !serverMacoMatrix) return;
    if (autoDefaultedTrainRef.current === selectedTrainId) return;
    const pick = serverMacoMatrix.approved_worst_case || serverMacoMatrix.worst_case;
    if (!pick) return;
    autoDefaultedTrainRef.current = selectedTrainId;
    setPreviousApiId(String(pick.api_id));
    setNextProductId(String(pick.product_id));
  }, [selectedTrainId, serverMacoMatrix]);

  const refreshCurrentGoverning = () => {
    if (!selectedTrainId) { setCurrentGoverning(null); return; }
    macoAPI.getCurrentGoverning(selectedTrainId)
      .then((res) => setCurrentGoverning(res?.data?.data || null))
      .catch(() => setCurrentGoverning(null));
  };
  useEffect(refreshCurrentGoverning, [selectedTrainId]);

  useEffect(() => { if (showGoverningESign) setTimeout(() => governingPasswordRef.current?.focus(), 80); }, [showGoverningESign]);

  const submitFinalizeGoverning = async () => {
    if (!governingWcPair) return;
    if (!governingESignData.password) return toast.error('Password required');
    if (!governingESignData.comment.trim()) return toast.error('E-signature comment is required');
    setFinalizingGoverning(true);
    try {
      await macoAPI.finalizeGoverning(selectedTrainId, {
        previous_api_id: governingWcPair.api_id,
        next_product_id: governingWcPair.product_id,
        esign_password: governingESignData.password,
        esign_comment: governingESignData.comment,
      });
      toast.success('Governing MACO signed and saved for this train');
      setShowGoverningESign(false);
      refreshCurrentGoverning();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to sign governing MACO');
    } finally {
      setFinalizingGoverning(false);
    }
  };

  const trainProducts = useMemo(() => {
    if (!selectedTrainId) return [];
    return products.filter((p) => String(p.equipment_train_id || '') === String(selectedTrainId));
  }, [products, selectedTrainId]);

  useEffect(() => {
    if (trainProducts.length > 0) {
      const isNextProductInList = trainProducts.some(p => String(p.product_id) === nextProductId);
      if (!isNextProductInList) {
        setNextProductId(String(trainProducts[0].product_id));
      }
    } else {
      setNextProductId('');
    }
  }, [trainProducts, nextProductId]);

  useEffect(() => {
    if (!previousApiId && apis.length) {
      setPreviousApiId(String(apis[0].api_id));
    }
  }, [apis, previousApiId]);

  const effectiveDoseSF = useMemo(() => {
    return activeLineRoute && doseSFByRoute[activeLineRoute] != null
      ? doseSFByRoute[activeLineRoute]
      : doseSafetyFactor;
  }, [activeLineRoute, doseSFByRoute, doseSafetyFactor]);

  // ── Unique lines available from loaded trains (for local line filter dropdown) ──
  const availableLines = useMemo(() => {
    const lines = new Set();
    trains.forEach(t => {
      const l = String(t.production_line || t.line_name || '').trim();
      if (l) lines.add(l);
    });
    return Array.from(lines).sort();
  }, [trains]);

  // ── Trains visible in the dropdown, filtered by local line selection ──────────
  const filteredTrains = useMemo(() => {
    if (!macoLineFilter || macoLineFilter === 'All') return trains;
    return trains.filter(t => {
      const l = String(t.production_line || t.line_name || '').trim();
      return l === macoLineFilter || l.includes(macoLineFilter);
    });
  }, [trains, macoLineFilter]);

  // ── Default to the first available section (no "All Sections" option) ─────────
  useEffect(() => {
    // Also replaces a value with no matching <option> (e.g. 'All', or a stale
    // header section) — otherwise the select silently shows the wrong entry.
    if (availableLines.length && !availableLines.includes(macoLineFilter)) {
      setMacoLineFilter(availableLines[0]);
    }
  }, [macoLineFilter, availableLines]);

  // ── Keep the selected train in sync with the (possibly filtered) train list ────
  // Prevents the <select> from rendering blank when selectedTrainId points at a
  // train that the current section filter has excluded from the option list.
  useEffect(() => {
    if (!filteredTrains.length) return;
    const stillValid = filteredTrains.some(t => String(t.train_id) === String(selectedTrainId));
    if (!stillValid) {
      setSelectedTrainId(String(filteredTrains[0].train_id));
    }
  }, [filteredTrains, selectedTrainId]);

  // ── Fetch contact surface area from train members when train changes ───────────
  useEffect(() => {
    if (!selectedTrainId) { setTrainSurfaceAreaCm2(null); return; }
    let cancelled = false;
    setTrainSurfaceLoading(true);
    equipmentAPI.getTrainFull(selectedTrainId)
      .then(res => {
        if (cancelled) return;
        const members = res?.data?.data?.members || res?.data?.members || [];
        const SA_TO_CM2 = { 'cm²':1,'cm2':1,'m²':10000,'m2':10000,'ft²':929.03,'ft2':929.03 };
        let total = 0;
        members.forEach(m => {
          const sa = parseFloat(m.surface_area || m.part_surface_area || 0);
          const unit = String(m.surface_area_unit || 'cm²').toLowerCase();
          if (sa > 0) total += sa * (SA_TO_CM2[unit] || 1);
        });
        setTrainSurfaceAreaCm2(total > 0 ? total : null);
      })
      .catch(() => { if (!cancelled) setTrainSurfaceAreaCm2(null); })
      .finally(() => { if (!cancelled) setTrainSurfaceLoading(false); });
    return () => { cancelled = true; };
  }, [selectedTrainId]);

  // ── Effective surface area: per-train contact SA (if available) else global ───
  const effectiveSurfaceAreaCm2 = useMemo(() => {
    const base = trainSurfaceAreaCm2 ?? surfaceAreaCm2;
    return Math.max(0.000001, base * (1 + trainAreaCompensationPct / 100));
  }, [trainSurfaceAreaCm2, surfaceAreaCm2, trainAreaCompensationPct]);

  // Read navigation state from Product Master's "View MACO Dose SF" link
  useEffect(() => {
    const state = location.state;
    if (!state) return;
    if (state.openTab === 'settings') {
      setActiveTab('settings');
      if (state.highlightRoute) {
        setNavHighlightRoute(state.highlightRoute);
        // Scroll to the highlighted row after a short delay to let the tab render
        setTimeout(() => {
          if (routeHighlightRef.current) {
            routeHighlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 400);
      }
    }
    if (state.nextProductId) {
      setNextProductId(String(state.nextProductId));
    }
  }, [location.state]);

  // Fetch risk context for the currently-selected next product (contextual only, non-fatal on failure)
  useEffect(() => {
    if (!nextProductId) {
      setNextProductRisk(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await riskAssessmentAPI.getAll({ product_id: nextProductId });
        if (cancelled) return;
        setNextProductRisk(res?.data?.data?.[0] || null);
      } catch {
        if (!cancelled) setNextProductRisk(null);
      }
    })();
    return () => { cancelled = true; };
  }, [nextProductId]);

  // Resolve the detergent LD50 actually used for this criterion: whichever detergent(s)
  // Cleaning Config has configured for this plant/line scope, looked up by name in
  // Detergent Master. More than one detergent in use -> take the lowest (most
  // conservative/most toxic) LD50 among those that have one set. Falls back to the
  // single global value in MACO Settings when no configured detergent has an LD50 yet
  // (e.g. right after upgrading, before anyone has filled it in).
  const detergentLd50Source = useMemo(() => {
    if (!configDetergentNames.length || !detergentList.length) return { value: detergentLd50, fromMaster: false };
    const matched = configDetergentNames
      .map((name) => detergentList.find((d) => String(d.detergent_name).toLowerCase() === String(name).toLowerCase()))
      .filter((d) => d && d.ld50_value != null && Number(d.ld50_value) > 0);
    if (!matched.length) return { value: detergentLd50, fromMaster: false };
    const lowest = matched.reduce((min, d) => Math.min(min, Number(d.ld50_value)), Infinity);
    return { value: lowest, fromMaster: true, detergentName: matched.find((d) => Number(d.ld50_value) === lowest)?.detergent_name };
  }, [configDetergentNames, detergentList, detergentLd50]);
  const effectiveDetergentLd50 = detergentLd50Source.value;

  const worstCaseData = useMemo(() => {
    if (!selectedTrain || !products.length || !apis.length) {
      return { matrix: {}, worstCase: null, productsOnTrain: [], apisToConsider: [] };
    }

    let productsOnTrain = products.filter((product) =>
      String(product.equipment_train_id) === String(selectedTrain.train_id) ||
      (Array.isArray(product.linked_trains) && product.linked_trains.some((train) => String(train.train_id) === String(selectedTrain.train_id)))
    );
    if (!productsOnTrain.length) {
      return { matrix: {}, worstCase: null, productsOnTrain: [], apisToConsider: [] };
    }

    // Only consider APIs from products linked to this train
    const trainApiIds = new Set();
    productsOnTrain.forEach(p => {
      (Array.isArray(p.apis) ? p.apis : []).forEach(a => {
        if (a.api_id) trainApiIds.add(String(a.api_id));
      });
    });
    let apisToConsider = trainApiIds.size > 0 ? apis.filter(a => trainApiIds.has(String(a.api_id))) : apis;
    let matrix = {};
    // `draftWorstCase` considers every pair (Draft/Pending included) — a preview
    // of what the worst case would be if everything on the train were approved
    // as-is. `worstCase` only considers pairs where the API, the product, and
    // every API on that product are Approved — this is the official,
    // validation-grade figure (persisted downstream for Acceptance Criteria) and
    // must not shift just because someone adds or edits a draft API/product.
    let draftWorstCase = { value: Infinity, api: null, product: null };
    let worstCase = { value: Infinity, api: null, product: null };

    apisToConsider.forEach(prevApi => {
      matrix[prevApi.api_id] = {};
      productsOnTrain.forEach(nextProd => {
        const results = calculateMacoValues({
          previousApi: prevApi,
          nextProduct: nextProd,
          hbelSafetyFactor,
          doseSafetyFactor: effectiveDoseSF,
          ppmLimit,
          toxSafetyFactor,
          detergentSafetyFactor,
          detergentLd50: effectiveDetergentLd50,
          detergentBodyWeight,
          detergentEnabled: criteriaEnabled.detergent,
          criteriaEnabled,
          surfaceAreaCm2: effectiveSurfaceAreaCm2,
        });

        const isProvisional = prevApi.status !== 'Approved' || nextProd.status !== 'Approved'
          || (Array.isArray(nextProd.apis) ? nextProd.apis : []).some(a => a.status !== 'Approved');
        matrix[prevApi.api_id][nextProd.product_id] = { ...results, isProvisional };

        if (results.governing > 0 && results.governing < draftWorstCase.value) {
          draftWorstCase = { value: results.governing, api: prevApi, product: nextProd, mar: results.governingMar, detergentMar: results.detergentActive ? results.detergentMar : null };
        }
        if (!isProvisional && results.governing > 0 && results.governing < worstCase.value) {
          worstCase = { value: results.governing, api: prevApi, product: nextProd, mar: results.governingMar, detergentMar: results.detergentActive ? results.detergentMar : null };
        }
      });
    });

    const serverDataIsCurrent = !settingsDirty && String(serverMacoMatrix?.train?.train_id) === String(selectedTrain?.train_id);
    if (serverDataIsCurrent) {
      productsOnTrain = serverMacoMatrix.products || productsOnTrain;
      apisToConsider = serverMacoMatrix.apis || apisToConsider;
      matrix = serverMacoMatrix.matrix || matrix;
      draftWorstCase = { value: serverMacoMatrix.worst_case?.value ?? Infinity,
        mar: serverMacoMatrix.worst_case?.mar ?? 0,
        api: apisToConsider.find(a => String(a.api_id) === String(serverMacoMatrix.worst_case?.api_id)) || null,
        product: productsOnTrain.find(p => String(p.product_id) === String(serverMacoMatrix.worst_case?.product_id)) || null };
      worstCase = serverMacoMatrix.approved_worst_case ? {
        value: serverMacoMatrix.approved_worst_case.value,
        mar: serverMacoMatrix.approved_worst_case.mar,
        api: apisToConsider.find(a => String(a.api_id) === String(serverMacoMatrix.approved_worst_case.api_id)) || null,
        product: productsOnTrain.find(p => String(p.product_id) === String(serverMacoMatrix.approved_worst_case.product_id)) || null,
      } : { value: Infinity, api: null, product: null };
    }

    // Persist governing MAR to localStorage for Acceptance Criteria page to
    // consume — only the Approved-only figure, so a draft addition never
    // silently moves an already-validated acceptance limit.
    if (worstCase.value !== Infinity && worstCase.mar > 0 && selectedTrain) {
      try {
        const savedMap = JSON.parse(localStorage.getItem('maco_governing_mar_map') || '{}');
        savedMap[selectedTrain.train_id] = {
          mar_per_cm2:  worstCase.mar,
          detergentMar: worstCase.detergentMar ?? null,
          maco_mg:      worstCase.value,
          api_name:     worstCase.api?.api_name  || '',
          product_name: worstCase.product?.product_name || '',
          train_name:   selectedTrain.train_name || '',
          production_line: selectedTrain.production_line || '',
          calculated_at: new Date().toISOString(),
        };
        localStorage.setItem('maco_governing_mar_map', JSON.stringify(savedMap));
      } catch {}
    }

    // Build API×API matrix: for each next-API, find its parent product and compute MACO
    const apiMatrix = {};
    const apiToProduct = {};  // map api_id -> product (using first product that contains that API)
    productsOnTrain.forEach(prod => {
      (Array.isArray(prod.apis) ? prod.apis : []).forEach(a => {
        if (a.api_id && !apiToProduct[String(a.api_id)]) {
          apiToProduct[String(a.api_id)] = prod;
        }
      });
    });
    apisToConsider.forEach(prevApi => {
      apiMatrix[prevApi.api_id] = {};
      apisToConsider.forEach(nextApi => {
        if (String(prevApi.api_id) === String(nextApi.api_id)) {
          apiMatrix[prevApi.api_id][nextApi.api_id] = null; // diagonal = blank
        } else {
          const nextProd = apiToProduct[String(nextApi.api_id)];
          if (nextProd) {
            apiMatrix[prevApi.api_id][nextApi.api_id] = matrix[prevApi.api_id]?.[nextProd.product_id] || null;
          }
        }
      });
    });

    return { matrix, apiMatrix, apiToProduct, worstCase, draftWorstCase, productsOnTrain, apisToConsider };
  }, [selectedTrain, products, apis, hbelSafetyFactor, effectiveDoseSF, ppmLimit, toxSafetyFactor, effectiveDetergentLd50, detergentBodyWeight, detergentSafetyFactor, criteriaEnabled, effectiveSurfaceAreaCm2, serverMacoMatrix, settingsDirty]);

  // Derive Pair Card Values
  const pairValues = useMemo(() => {
    if (!previousApiId || !nextProductId || !worstCaseData.matrix[previousApiId]) return null;
    return worstCaseData.matrix[previousApiId][nextProductId] || null;
  }, [previousApiId, nextProductId, worstCaseData]);

  const ai = useAIAction();

  const getExportHeader = (reportName = 'MACO Calculation Report') => {
     let savedUser = null;
     try {
       const u = JSON.parse(localStorage.getItem('user') || '{}');
       savedUser = u;
     } catch(e) {}

     const exportedAt = new Date();
     const exportedBy = savedUser?.username || savedUser?.full_name || savedUser?.name || savedUser?.employee_id || 'Unknown User';
     const exportedByRole = savedUser?.role || 'Unknown Role';
     const systemName = 'PrimeClean';
     const systemHost = window?.location?.origin || 'Unknown Origin';
     const systemNode = window?.location?.hostname || 'Unknown Host';
     const unitName = String(localStorage.getItem('activePlant') || 'N/A').trim() || 'N/A';
     const productionLine = String(localStorage.getItem('activeLine') || 'All Sections').trim();

     return [
        ['PrimeClean MACO Calculation Export'],
        ['21 CFR Part 11 Compliance Context'],
        [],
        ['Report ID', `MAC-EXP-${exportedAt.getTime()}`],
        ['Report Name', reportName],
        ['Unit Name', unitName],
        ['Section', productionLine],
        ['Equipment Train', `${selectedTrain?.train_code} - ${selectedTrain?.train_name}`],
        ['Exported At', exportedAt.toLocaleString()],
        ['Exported At (ISO)', exportedAt.toISOString()],
        ['Exported By', exportedBy],
        ['Exported By Role', exportedByRole],
        ['System Name', systemName],
        ['System Node', systemNode],
        ['System Origin', systemHost],
        ['Record Count', `${worstCaseData.apisToConsider.length} APIs x ${worstCaseData.productsOnTrain.length} Products`],
        ['System Note', 'Electronic record export with trace metadata for audit review.'],
        ['']
     ];
  };

  const toDataUrlIfPossible = (src) => new Promise((resolve) => {
    if (!src) return resolve(null);
    const trimmed = String(src).trim();
    if (!trimmed) return resolve(null);
    if (trimmed.startsWith('data:image/')) return resolve(trimmed);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width; canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = trimmed;
  });

  const truncatePdfHeaderText = (doc, value, maxWidth) => {
    const text = String(value || '').trim();
    if (!text) return '-';
    if (doc.getTextWidth(text) <= maxWidth) return text;
    let truncated = text;
    while (truncated.length > 0 && doc.getTextWidth(`${truncated}...`) > maxWidth) truncated = truncated.slice(0, -1);
    return truncated ? `${truncated}...` : '...';
  };

  const drawPdfGridTable = (doc, { headers, rows, startY, topMargin, bottomMargin, leftMargin, rightMargin, alternateRowColor, fontSize, colWidthWeights, drawHeaderFooter, isHighlighted }) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const tableWidth = pageWidth - leftMargin - rightMargin;
    const colCount = headers.length;
    const normalizedWeights = Array.isArray(colWidthWeights) && colWidthWeights.length === colCount
      ? colWidthWeights.map(w => (Number.isFinite(Number(w)) && Number(w) > 0 ? Number(w) : 1))
      : Array.from({ length: colCount }, () => 1);
    const totalWeight = normalizedWeights.reduce((sum, w) => sum + w, 0) || colCount;
    const colWidths = normalizedWeights.map(w => (tableWidth * w) / totalWeight);
    const cellPadding = 3;
    const lineHeight = Math.max(8, fontSize + 2);
    const minRowHeight = Math.max(14, lineHeight + 2);
    const headerFontSize = Math.max(fontSize + 0.6, 6.4);
    const headerLineHeight = Math.max(8, headerFontSize + 1.8);
    const originalFontSize = doc.getFontSize();
    
    doc.setFontSize(headerFontSize);
    const wrappedHeaderCells = headers.map((header, i) =>
      doc.splitTextToSize(String(header || '').trim(), Math.max(colWidths[i] - (cellPadding * 2), 10)).map(l => String(l || '').trim()).filter(Boolean)
    );
    doc.setFontSize(originalFontSize);
    
    const maxHeaderLines = wrappedHeaderCells.reduce((acc, lines) => Math.max(acc, lines.length || 1), 1);
    const headerHeight = Math.max(minRowHeight + 4, (maxHeaderLines * headerLineHeight) + (cellPadding * 2) + 2);
    let y = startY;

    const drawHeaderRow = () => {
      doc.setFillColor(255, 255, 255);
      doc.rect(leftMargin, y, tableWidth, headerHeight, 'F');
      doc.setDrawColor(180, 192, 208);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(headerFontSize);
      doc.setFont('helvetica', 'bold');
      let x = leftMargin;
      headers.forEach((_, i) => {
        doc.rect(x, y, colWidths[i], headerHeight, 'S');
        const lines = Array.isArray(wrappedHeaderCells[i]) && wrappedHeaderCells[i].length > 0 ? wrappedHeaderCells[i] : ['-'];
        lines.forEach((line, li) => {
          const textY = y + cellPadding + headerFontSize + (li * headerLineHeight);
          if (textY <= y + headerHeight - cellPadding) doc.text(line, x + cellPadding, textY);
        });
        x += colWidths[i];
      });
      doc.setFont('helvetica', 'normal');
      y += headerHeight;
    };

    const addNewPageWithHeader = () => {
      doc.addPage();
      if (typeof drawHeaderFooter === 'function') drawHeaderFooter();
      y = topMargin;
      drawHeaderRow();
    };

    if (y + headerHeight > pageHeight - bottomMargin) addNewPageWithHeader();
    else drawHeaderRow();

    rows.forEach((row, rowIndex) => {
      const wrappedCells = row.map((cell, i) => doc.splitTextToSize(String(cell ?? '-'), colWidths[i] - (cellPadding * 2)));
      const maxLines = wrappedCells.reduce((acc, lines) => Math.max(acc, lines.length || 1), 1);
      const rowHeight = Math.max(minRowHeight, (maxLines * lineHeight) + (cellPadding * 2));

      if (y + rowHeight > pageHeight - bottomMargin) addNewPageWithHeader();
      if (rowIndex % 2 === 1) {
        doc.setFillColor(...alternateRowColor);
        doc.rect(leftMargin, y, tableWidth, rowHeight, 'F');
      }

      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fontSize);

      let x = leftMargin;
      wrappedCells.forEach((lines, i) => {
        const hot = typeof isHighlighted === 'function' && isHighlighted(rowIndex, i);
        doc.setDrawColor(203, 213, 225);
        if (hot) { doc.setFillColor(255, 242, 240); doc.rect(x, y, colWidths[i], rowHeight, 'FD'); }
        else doc.rect(x, y, colWidths[i], rowHeight);
        doc.setTextColor(...(hot ? [207, 19, 34] : [15, 23, 42]));
        doc.setFont('helvetica', hot ? 'bold' : 'normal');
        const lineArr = Array.isArray(lines) && lines.length > 0 ? lines : ['-'];
        lineArr.forEach((line, li) => {
          const textY = y + cellPadding + fontSize + (li * lineHeight);
          if (textY <= y + rowHeight - cellPadding) doc.text(line, x + cellPadding, textY);
        });
        x += colWidths[i];
      });
      y += rowHeight;
    });
    return y;
  };

  const exportExcelTabs = () => {
    if (!selectedTrain || !worstCaseData.productsOnTrain.length || !worstCaseData.apisToConsider.length) {
        toast.error('No matrix data available to export.');
        return;
    }

    const wb = XLSX.utils.book_new();

    // Summary Sheet
    const summaryData = [
       ...getExportHeader(),
       ['MACO CALCULATION SUMMARY'],
       [],
       ['Train Code', selectedTrain?.train_code],
       ['Train Name', selectedTrain?.train_name],
       ['Equipment Surface Area (cm²)', fmt(surfaceAreaCm2, 2)],
       ['Exported At', new Date().toLocaleString()],
       [],
       ['Overall Worst-Case Scenario'],
    ];

    if (worstCaseData.worstCase && worstCaseData.worstCase.value !== Infinity) {
       summaryData.push(['Governing MACO (mg)', fmt(worstCaseData.worstCase.value, 6)]);
       summaryData.push(['Governing MAR/cm² (mcg)', fmt(worstCaseData.worstCase.mar, 6)]);
       summaryData.push(['Previous API', worstCaseData.worstCase.api?.api_name]);
       summaryData.push(['Next Product', worstCaseData.worstCase.product?.product_name]);
    }

    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    wsSummary['!cols'] = [{ wch: 30 }, { wch: 40 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Matrix Sheets Generator function
    const appendMatrixSheet = (criterionKey, name) => {
        const isApiMode = matrixMode === 'api';
        const columnsData = isApiMode ? worstCaseData.apisToConsider : worstCaseData.productsOnTrain;

        const buildData = (marFlag) => {
            return worstCaseData.apisToConsider.map(prevApi => {
                const row = { [isApiMode ? 'Previous API \\ Next API' : 'Previous API \\ Next Product']: prevApi.api_name };
                columnsData.forEach(nextEntity => {
                    let res = null;
                    let keyName = '';
                    if (isApiMode) {
                        keyName = nextEntity.api_name;
                        res = worstCaseData.apiMatrix?.[prevApi.api_id]?.[nextEntity.api_id];
                    } else {
                        keyName = nextEntity.product_name || `product_${nextEntity.product_id}`;
                        res = worstCaseData.matrix?.[prevApi.api_id]?.[nextEntity.product_id];
                    }
                    
                    const valueKey = marFlag ? criterionKey + 'Mar' : criterionKey;
                    
                    if (isApiMode && String(prevApi.api_id) === String(nextEntity.api_id)) {
                        row[keyName] = ''; // Blank diagonal
                    } else {
                        row[keyName] = res && res[valueKey] > 0 ? fmt(res[valueKey], 6) : '-';
                    }
                });
                return row;
            });
        };

        const dataForMaco = buildData(false);
        const headerMaco = [ ...getExportHeader(`MACO Matrix (${matrixMode.toUpperCase()}) - ${name} limits (mg)`), [`MACO Limits (mg) - ${name}`], [] ];
        const wsMaco = XLSX.utils.aoa_to_sheet(headerMaco);
        XLSX.utils.sheet_add_json(wsMaco, dataForMaco, { origin: `A${headerMaco.length + 1}`, skipHeader: false });
        const leastNote = findLeastCells(criterionKey, 'maco', (v) => `${fmt(v, 6)} mg`).text;
        if (leastNote) XLSX.utils.sheet_add_aoa(wsMaco, [[], ['Least value'], ...leastNote.split('\n').map(l => [l])], { origin: `A${headerMaco.length + dataForMaco.length + 3}` });
        if (dataForMaco.length > 0) wsMaco['!cols'] = Object.keys(dataForMaco[0]).map(k => ({ wch: Math.max(String(k).length + 5, 20) }));
        XLSX.utils.book_append_sheet(wb, wsMaco, `${name} MACO`);

        const dataForMar = buildData(true);
        const headerMar = [ ...getExportHeader(`MAR Matrix (${matrixMode.toUpperCase()}) - ${name} limits (mcg/cm²)`), [`MAR Limits (mcg/cm²) - ${name}`], [] ];
        const wsMar = XLSX.utils.aoa_to_sheet(headerMar);
        XLSX.utils.sheet_add_json(wsMar, dataForMar, { origin: `A${headerMar.length + 1}`, skipHeader: false });
        if (dataForMar.length > 0) wsMar['!cols'] = Object.keys(dataForMar[0]).map(k => ({ wch: Math.max(String(k).length + 5, 20) }));
        XLSX.utils.book_append_sheet(wb, wsMar, `${name} MAR-cm2`);

    };

    appendMatrixSheet('governing', 'Governing');
    if (criteriaEnabled.hbel) appendMatrixSheet('hbel', 'Health Base');
    if (criteriaEnabled.dose) appendMatrixSheet('dose', 'Dose Based');
    if (criteriaEnabled.ppm) appendMatrixSheet('ppm', '10 PPM');
    if (criteriaEnabled.tox) appendMatrixSheet('tox', 'Toxicological');
    if (criteriaEnabled.detergent) appendMatrixSheet('detergent', 'Detergent');

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    XLSX.writeFile(wb, `MACO_Full_Export_${selectedTrain.train_code}_${timestamp}.xlsx`);
    toast.success('Multi-sheet Excel report exported successfully.');
  };

  const exportPDFTheme = async (sections = { summary: true, governing: true, hbel: true, dose: true, ppm: true, tox: true, detergent: true }) => {
    // Header/footer from Settings → App Configuration → Reports & Documents.
    const reportSettings = withReportProfile('maco', 'MACO Calculation Report');
    if (!selectedTrain || !worstCaseData.productsOnTrain.length || !worstCaseData.apisToConsider.length) {
        toast.error('No data available to export.');
        return;
    }
    if (!Object.values(sections).some(Boolean)) {
        toast.warning('Select at least one section to export.');
        return;
    }

    try {
      const [clientLogoData, primeLogoData] = await Promise.all([
        toDataUrlIfPossible(reportSettings.report_client_logo),
        toDataUrlIfPossible(reportSettings.report_primeclean_logo)
      ]);

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const headerTop = 16;
      const headerRowSplit = 44;
      const headerBottom = 82;
      const leftLogoWidth = 130;
      const rightLogoWidth = 130;
      const bodyTop = headerBottom + 8;
      const footerTop = pageHeight - 42;

      let savedUser = null;
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        savedUser = u;
      } catch(e) {}
      const exportedAt = new Date();
      const exportedBy = savedUser?.username || savedUser?.full_name || savedUser?.name || 'Unknown User';
      const exportedByRole = savedUser?.role || 'Unknown Role';
      const systemName = 'PrimeClean';
      const systemNode = window?.location?.hostname || 'Unknown Host';
      const unitName = String(localStorage.getItem('activePlant') || 'N/A').trim() || 'N/A';
      const productionLine = String(localStorage.getItem('activeLine') || 'All Sections').trim();
      const reportId = `MAC-EXP-${exportedAt.getTime()}`;
      const exportedAtDisplay = exportedAt.toLocaleString();

      const drawHeaderFooter = () => {
        const pageInfo = doc.internal.getCurrentPageInfo ? doc.internal.getCurrentPageInfo() : null;
        const pageNo = pageInfo?.pageNumber || doc.internal.getNumberOfPages();
        const totalPages = doc.internal.getNumberOfPages();

        doc.setDrawColor(80, 80, 80);
        doc.rect(20, headerTop, pageWidth - 40, headerBottom - headerTop);
        doc.line(20 + leftLogoWidth, headerTop, 20 + leftLogoWidth, headerRowSplit);
        doc.line(pageWidth - 20 - rightLogoWidth, headerTop, pageWidth - 20 - rightLogoWidth, headerRowSplit);
        doc.line(20, headerRowSplit, pageWidth - 20, headerRowSplit);

        if (clientLogoData) {
          doc.addImage(clientLogoData, 'PNG', 26, headerTop + 7, leftLogoWidth - 12, 18);
        } else {
          doc.setFontSize(8);
          doc.text('Client Logo', 26, headerTop + 19);
        }

        if (primeLogoData) {
          doc.addImage(primeLogoData, 'PNG', pageWidth - 20 - rightLogoWidth + 6, headerTop + 7, rightLogoWidth - 12, 18);
        } else {
          doc.setFontSize(8);
          doc.text('PrimeClean Logo', pageWidth - 20 - rightLogoWidth + 8, headerTop + 19);
        }

        doc.setFontSize(8.6);
        doc.setTextColor(15, 23, 42);
        doc.text(reportSettings.report_client_name || 'Client Name', pageWidth / 2, headerTop + 16, { align: 'center' });
        doc.setFontSize(7.6);
        doc.text(unitReportAddress(reportSettings) || 'Client Address', pageWidth / 2, headerTop + 27, { align: 'center' });

        doc.setFontSize(8.5);
        doc.text(truncatePdfHeaderText(doc, `Document Name: ${reportSettings.report_document_name || 'MACO Calculation Report'}`, 220), 26, headerRowSplit + 14);
        doc.text(truncatePdfHeaderText(doc, `Unit Name: ${unitName}`, 210), pageWidth / 2, headerRowSplit + 14, { align: 'center' });
        doc.text(`Effective Date: ${exportedAtDisplay}`, pageWidth - 24, headerRowSplit + 14, { align: 'right' });
        doc.text(truncatePdfHeaderText(doc, `Equipment Train: ${selectedTrain?.train_code} - ${selectedTrain?.train_name}`, 260), 26, headerRowSplit + 26);
        doc.text(truncatePdfHeaderText(doc, `Section: ${productionLine}`, 210), pageWidth / 2, headerRowSplit + 26, { align: 'center' });
        doc.text(truncatePdfHeaderText(doc, `Document & Rev. No.: ${reportSettings.report_document_rev_no || '01'}`, 210), pageWidth / 2, headerRowSplit + 38, { align: 'center' });

        doc.line(20, footerTop, pageWidth - 20, footerTop);
        doc.setFontSize(7.2);
        doc.text(`Report ID: ${reportId}  Generated By: ${exportedBy} (${exportedByRole})  System: ${systemName} | ${systemNode}`, pageWidth / 2, pageHeight - 27, { align: 'center' });
        if (reportSettings.report_format_no_maco) {
          doc.text(`Format No: ${reportSettings.report_format_no_maco}`, pageWidth / 2, pageHeight - 19, { align: 'center' });
        }
        doc.setFontSize(8);
        doc.text(`Page ${pageNo} of ${totalPages}`, 24, pageHeight - 12);
        doc.text(`Print: ${exportedAtDisplay}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
        doc.text(reportSettings.report_footer_tagline || 'Data is generated and handled by PrimeClean.', pageWidth - 24, pageHeight - 12, { align: 'right' });
      };

      const isApiMode = matrixMode === 'api';
      const columnsData = isApiMode ? worstCaseData.apisToConsider : worstCaseData.productsOnTrain;

      const addMatrixPage = (criterionKey, title, addNewPage) => {
        if (addNewPage) { doc.addPage(); drawHeaderFooter(); }
        let y = bodyTop + 10;
        doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(15, 23, 42);
        doc.text(title, 40, y);
        y += 18;
        const headers = [
          isApiMode ? 'Prev API \\ Next API' : 'Prev API \\ Next Product',
          ...columnsData.map(c => isApiMode ? c.api_name : c.product_name)
        ];
        const rows = worstCaseData.apisToConsider.map(prevApi => [
          prevApi.api_name,
          ...columnsData.map(nextEntity => {
            if (isApiMode) {
              if (String(prevApi.api_id) === String(nextEntity.api_id)) return '';
              const res = worstCaseData.apiMatrix?.[prevApi.api_id]?.[nextEntity.api_id];
              return (res && res[criterionKey] > 0) ? fmt(res[criterionKey], 4) : '-';
            } else {
              const res = worstCaseData.matrix?.[prevApi.api_id]?.[nextEntity.product_id];
              return (res && res[criterionKey] > 0) ? fmt(res[criterionKey], 4) : '-';
            }
          })
        ]);
        // Same least cell(s) and reasoning as the red cell + hover card on screen.
        const least = findLeastCells(criterionKey, 'maco', (v) => `${fmt(v, 4)} mg`);
        const isHighlighted = (rowIndex, colIndex) => colIndex > 0 && least.keys.has(
          `${worstCaseData.apisToConsider[rowIndex].api_id}|${isApiMode ? columnsData[colIndex - 1].api_id : columnsData[colIndex - 1].product_id}`);
        y = drawPdfGridTable(doc, { headers, rows, startY: y, topMargin: bodyTop, bottomMargin: 40, leftMargin: 20, rightMargin: 20, alternateRowColor: [248, 250, 252], fontSize: 7.5, drawHeaderFooter, isHighlighted });

        if (least.text) {
          const boxWidth = doc.internal.pageSize.getWidth() - 40;
          const lines = doc.splitTextToSize(least.text.replace(/→/g, '->'), boxWidth - 20);
          const boxHeight = 24 + lines.length * 10;
          y += 14;
          if (y + boxHeight > doc.internal.pageSize.getHeight() - 40) { doc.addPage(); drawHeaderFooter(); y = bodyTop + 10; }
          doc.setDrawColor(255, 77, 79); doc.setFillColor(255, 242, 240);
          doc.rect(20, y, boxWidth, boxHeight, 'FD');
          doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(207, 19, 34);
          doc.text('Least value', 30, y + 13);
          doc.setFont('helvetica', 'normal'); doc.setTextColor(15, 23, 42); doc.setFontSize(8);
          doc.text(lines, 30, y + 25);
        }
      };

      // First page header
      drawHeaderFooter();
      let needNewPage = false;

      if (sections.summary) {
        let yPos = bodyTop + 10;
        doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(15, 23, 42);
        doc.text(`Overall Worst-Case Scenario — ${selectedTrain.train_name}`, 40, yPos); yPos += 20;
        doc.setFontSize(10); doc.setFont('helvetica', 'normal');
        if (worstCaseData.worstCase && worstCaseData.worstCase.value !== Infinity) {
          doc.text(`Governing MACO (mg): ${fmt(worstCaseData.worstCase.value, 6)}`, 40, yPos); yPos += 14;
          doc.text(`Governing MAR/cm² (mcg): ${fmt(worstCaseData.worstCase.mar, 6)}`, 40, yPos); yPos += 14;
          doc.text(`Previous API: ${worstCaseData.worstCase.api?.api_name}`, 40, yPos); yPos += 14;
          doc.text(`Next Product: ${worstCaseData.worstCase.product?.product_name}`, 40, yPos);
        } else {
          doc.text('No worst-case found.', 40, yPos);
        }
        needNewPage = true;
      }

      if (sections.governing) { addMatrixPage('governing', 'Governing MACO Matrix (mg)', needNewPage); needNewPage = true; }
      if (sections.hbel && criteriaEnabled.hbel) { addMatrixPage('hbel', 'Health Base (ADE/PDE) MACO Matrix (mg)', needNewPage); needNewPage = true; }
      if (sections.dose && criteriaEnabled.dose) { addMatrixPage('dose', 'Dose-Based (0.1%) MACO Matrix (mg)', needNewPage); needNewPage = true; }
      if (sections.ppm && criteriaEnabled.ppm) { addMatrixPage('ppm', '10 PPM MACO Matrix (mg)', needNewPage); needNewPage = true; }
      if (sections.tox && criteriaEnabled.tox) { addMatrixPage('tox', 'Toxicological MACO Matrix (mg)', needNewPage); needNewPage = true; }
      if (sections.detergent && criteriaEnabled.detergent) { addMatrixPage('detergent', 'Detergent Toxicity MACO Matrix (mg)', needNewPage); }

      const fileStamp = exportedAt.toISOString().replace(/[:.]/g, '-');
      doc.save(`MACO_Calculation_Report_${selectedTrain.train_code}_${fileStamp}.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    }
  };

  // ── Unit display helpers ───────────────────────────────────────────────────
  const macoUnitLabel = macoDisplayUnit === 'mcg' ? 'µg' : 'mg';
  const marUnitLabel  = marDisplayUnit  === 'mg/cm2' ? 'mg/cm²' : 'µg/cm²';

  const displayMacoVal = (valueMg) => {
    if (!Number.isFinite(valueMg) || valueMg <= 0) return null;
    return macoDisplayUnit === 'mcg' ? valueMg * 1000 : valueMg;
  };
  const displayMarVal = (valueMcgCm2) => {
    if (!Number.isFinite(valueMcgCm2) || valueMcgCm2 <= 0) return null;
    return marDisplayUnit === 'mg/cm2' ? valueMcgCm2 / 1000 : valueMcgCm2;
  };
  const fmtDisplay = (val, digits = 6) => val !== null ? val.toFixed(digits) : '—';

  // ── Least-value highlight: the red cell(s) in the matrix and their hover reason ──
  const CRITERION_LABELS = { governing: 'Governing (min)', hbel: 'Health-Based (PDE)', dose: 'Dose-Based (0.1%)', ppm: '10 ppm', tox: 'Toxicological (LD50/NOEL)', detergent: 'Detergent' };
  const sameValue = (a, b) => Math.abs(a - b) <= Math.max(Math.abs(a), Math.abs(b)) * 1e-9;
  const governingBasisOf = (res) => res?.governingBasis
    || ['hbel', 'dose', 'ppm', 'tox'].find(k => criteriaEnabled[k] && res?.[k] > 0 && sameValue(res[k], res.governing))
    || null;
  // Finds the least value across the whole displayed matrix (ties all count) and
  // the hover text explaining why it was selected.
  const findLeastCells = (criterionKey, unit = 'maco', formatValue = (v) => fmt(v, 4)) => {
    const isApiMode = matrixMode === 'api';
    const valueKey = unit === 'mar' ? `${criterionKey}Mar` : criterionKey;
    const { apisToConsider = [], productsOnTrain = [], apiMatrix = {}, apiToProduct = {}, matrix = {} } = worstCaseData;
    const cells = [];
    (isApiMode ? apisToConsider : productsOnTrain).forEach(next => {
      const nextKey = String(isApiMode ? next.api_id : next.product_id);
      apisToConsider.forEach(prev => {
        if (isApiMode && String(prev.api_id) === nextKey) return;
        const res = isApiMode ? apiMatrix[prev.api_id]?.[next.api_id] : matrix[prev.api_id]?.[next.product_id];
        if (res && res[valueKey] > 0) cells.push({ prev, next, nextKey, res, product: isApiMode ? apiToProduct[nextKey] : next });
      });
    });
    if (!cells.length) return { keys: new Set(), rows: [], notes: [], text: '' };
    const minValue = Math.min(...cells.map(c => c.res[valueKey]));
    const winners = cells.filter(c => sameValue(c.res[valueKey], minValue));
    const basis = criterionKey === 'governing' ? governingBasisOf(winners[0].res) : criterionKey;
    const pairNames = winners.map(w => `${w.prev.api_name} → ${isApiMode ? w.next.api_name : w.next.product_name}`);
    const rows = [
      { label: 'Least value', value: formatValue(minValue) },
      ...(basis ? [{ label: 'Limiting criterion', value: CRITERION_LABELS[basis] }] : []),
      { label: winners.length > 1 ? `Pairs (${winners.length}, tied)` : 'Pair', value: pairNames.join('\n') },
    ];
    const notes = [];
    if (winners.length > 1) {
      const sameProduct = new Set(winners.map(w => String(w.product?.product_id))).size === 1;
      notes.push(basis === 'ppm'
        ? 'Tie: the 10 ppm limit depends only on the next batch size, not on the previous API.'
        : sameProduct && isApiMode
          ? `Tie: the next APIs belong to the same product (${winners[0].product?.product_name}), whose batch size and max daily dose set the MACO.`
          : 'Tie: these pairs give the same value.');
    }
    if (winners.some(w => w.res.isProvisional)) notes.push('Provisional: involves a Draft/Pending API or product.');
    const text = [...rows.map(r => `${r.label}: ${r.value.replace(/\n/g, '; ')}`), ...notes].join('\n');
    return { keys: new Set(winners.map(w => `${w.prev.api_id}|${w.nextKey}`)), rows, notes, text };
  };

  const [detergentPage, setDetergentPage] = useState(1);
  const [detergentPageSize, setDetergentPageSize] = useState(25);
  const detergentTotalPages = Math.max(1, Math.ceil(detergentList.length / detergentPageSize));
  useEffect(() => { if (detergentPage > detergentTotalPages) setDetergentPage(detergentTotalPages); }, [detergentPage, detergentTotalPages]);
  const paginatedDetergents = detergentList.slice((detergentPage - 1) * detergentPageSize, detergentPage * detergentPageSize);

  const DET_IMPORT_HEADERS = { 'detergent name': 'detergent_name', category: 'category', concentration: 'concentration', 'ld50 (mg/kg)': 'ld50_value', ld50: 'ld50_value', notes: 'notes' };
  const handleDetImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      let rawRows;
      if (file.name.toLowerCase().endsWith('.csv')) {
        rawRows = await new Promise((resolve, reject) => Papa.parse(file, { header: true, skipEmptyLines: true, complete: (r) => resolve(r.data), error: reject }));
      } else {
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array' });
        rawRows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      }
      const rows = rawRows.map((raw) => {
        const row = {};
        Object.entries(raw).forEach(([header, value]) => {
          const field = DET_IMPORT_HEADERS[String(header).trim().toLowerCase()] || String(header).trim().toLowerCase().replace(/\s+/g, '_');
          row[field] = typeof value === 'string' ? value.trim() : value;
        });
        return row;
      }).filter((r) => Object.values(r).some((v) => String(v || '').trim() !== ''));
      if (!rows.length) { toast.error('No data rows found in file'); return; }
      setDetImportPreview({ fileName: file.name, rows });
      setDetImportResult(null);
    } catch (error) {
      toast.error('Failed to parse file. Use the template format.');
    } finally {
      event.target.value = '';
    }
  };
  const downloadDetImportTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([{ 'Detergent Name': 'Caustic Soda', Category: 'Alkaline', Concentration: '2% w/v', 'LD50 (mg/kg)': 2000, Notes: '' }]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Detergents');
    XLSX.writeFile(wb, 'detergent_master_import_template.xlsx');
  };
  const confirmDetImport = async () => {
    setDetImporting(true);
    try {
      const response = await detergentMasterAPI.importRows({ rows: detImportPreview.rows });
      setDetImportResult(response?.data?.data || null);
      setDetImportPreview(null);
      const detRes = await detergentMasterAPI.getAll();
      setDetergentList(detRes?.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Import failed');
    } finally {
      setDetImporting(false);
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  const formatMatrixValue = (v) => {
    const converted = matrixUnit === 'maco' ? displayMacoVal(v) : displayMarVal(v);
    return converted === null ? '—' : `${fmtDisplay(converted, 4)} ${matrixUnit === 'maco' ? macoUnitLabel : marUnitLabel}`;
  };
  const leastCells = activeTab === 'matrix' ? findLeastCells(matrixCriterion, matrixUnit, formatMatrixValue) : { keys: new Set(), rows: [], notes: [], text: '' };
  const leastTipHandlers = (isLeast) => isLeast ? {
    onMouseEnter: (e) => { const r = e.currentTarget.getBoundingClientRect(); setLeastTip({ x: r.left + r.width / 2, top: r.top, bottom: r.bottom }); },
    onMouseLeave: () => setLeastTip(null),
  } : {};

  return (
    <div>
      <div>
        <div className="maco-page-header" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '10px 16px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <div className="maco-page-heading" style={{ flex: '1 1 300px', minWidth: '240px' }}>
            <h2 className="page-title" style={{ margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ backgroundColor: '#0ea5e9', color: '#fff', borderRadius: '4px', padding: '4px 8px', fontSize: 'var(--fs-lg)', fontWeight: 'bold' }}>M</span>
              MACO Calculator
            </h2>
            <p className="page-subtitle">Calculate governing MACO/MAR-per-cm² limits per equipment train across the full previous-API x next-product changeover matrix.</p>
          </div>
          <div className="maco-toolbar" style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', flex: '1 1 620px', minWidth: 0, paddingBottom: '2px' }}>
            {/* Local line filter — overrides header for MACO page only */}
            <div className="maco-toolbar-section" style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: '0 1 210px', minWidth: '175px' }}>
              <label style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', whiteSpace: 'nowrap', userSelect: 'none' }}>Section:</label>
              <select
                className="form-control"
                style={{ width: '100%', minWidth: 0, padding: '5px 8px', fontSize: 'var(--fs-sm)', flex: '1 1 auto' }}
                value={macoLineFilter}
                onChange={e => { setMacoLineFilter(e.target.value); setSelectedTrainId(''); setNextProductId(''); }}
              >
                {!macoLineFilter && <option value="">Select Section</option>}
                {availableLines.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>

            {/* Equipment train — filtered by local line selection */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: '0 1 290px', minWidth: '210px' }}>
            <label style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', whiteSpace: 'nowrap', userSelect: 'none' }}>Train:</label>
            <select
              className="form-control maco-toolbar-train"
              style={{ width: '100%', minWidth: 0, maxWidth: '250px', flex: '1 1 auto', padding: '5px 8px', fontSize: 'var(--fs-sm)' }}
              value={selectedTrainId}
              title={selectedTrain ? `${selectedTrain.train_code} - ${selectedTrain.train_name}` : undefined}
              onChange={e => { setSelectedTrainId(e.target.value); setNextProductId(''); }}
            >
              <option value="">Select Equipment Train</option>
              {filteredTrains.map(t => (
                <option key={t.train_id} value={t.train_id}>{t.train_code} - {t.train_name}</option>
              ))}
            </select>
            </div>

            {/* Surface area badge */}
            {selectedTrainId && (
              <div className="maco-toolbar-badge" style={{ fontSize: 'var(--fs-sm)', padding: '4px 10px', borderRadius: '6px',
                            background: trainSurfaceAreaCm2 ? 'var(--success-bg)' : 'var(--warning-bg)',
                            border: `1px solid ${trainSurfaceAreaCm2 ? 'var(--success-border)' : 'var(--warning-border)'}`,
                            color: trainSurfaceAreaCm2 ? 'var(--success-text)' : 'var(--warning-text)',
                            whiteSpace: 'nowrap' }}>
                {trainSurfaceLoading
                  ? 'Loading SA…'
                  : trainSurfaceAreaCm2
                    ? `Train SA: ${effectiveSurfaceAreaCm2.toFixed(1)} cm² (contact)`
                    : `SA: ${effectiveSurfaceAreaCm2.toFixed(1)} cm² (global setting)`}
              </div>
            )}

            {/* Risk context chip — links to Risk Assessment for the selected next product */}
            {nextProductRisk && (() => {
              const level = String(nextProductRisk.risk_level || '');
              const isHighCritical = level === 'Critical' || level === 'High';
              const isMedium = level === 'Medium';
              const bg = isHighCritical ? 'var(--danger-bg)' : isMedium ? 'var(--warning-bg)' : 'var(--success-bg)';
              const text = isHighCritical ? 'var(--danger-text)' : isMedium ? 'var(--warning-text)' : 'var(--success-text)';
              const border = isHighCritical ? 'var(--danger-border)' : isMedium ? 'var(--warning-border)' : 'var(--success-border)';
              return (
                <div
                  className="maco-toolbar-badge"
                  style={{ fontSize: 'var(--fs-sm)', padding: '4px 10px', borderRadius: '6px',
                            background: bg, border: `1px solid ${border}`, color: text,
                            whiteSpace: 'nowrap', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={() => navigate('/risk-assessment?product_id=' + nextProductId)}
                  title="View Risk Assessment"
                >
                  {isHighCritical && <FaExclamationTriangle />}
                  Risk: {nextProductRisk.risk_level} ({nextProductRisk.total_risk_score})
                </div>
              );
            })()}
          </div>
        </div>

        <div className="stat-card-grid" style={{ marginTop: '16px' }}>
          <StatCard compact title="Products" value={products.length} icon={FaCapsules} color="primary" />
          <StatCard compact title="APIs" value={apis.length} icon={FaFlask} color="success" />
          <StatCard compact title="Equipment Trains" value={trains.length} icon={FaLayerGroup} color="info" />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #e5e7eb)', marginTop: '16px', padding: '0 20px', gap: '20px', overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: '20px' }}>
            {[
              { key: 'matrix', icon: <FaTable />, label: 'Matrix' },
              { key: 'pairCard', icon: <FaIdCard />, label: 'Pair Card' },
              { key: 'summary', icon: <FaListAlt />, label: 'Summary' },
              { key: 'detergents', icon: <FaFlask />, label: 'Detergents' },
              { key: 'settings', icon: <FaCog />, label: 'Settings' }
            ].map(t => (
              <button key={t.key} type="button" className={`lms-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>{t.icon} {t.label}</button>
            ))}
          </div>
          <div className="maco-export-actions" style={{ display: 'flex', gap: '10px', paddingBottom: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
             <button
               className="btn btn-outline btn-sm"
               type="button"
               disabled={!pairValues}
               title={pairValues ? 'AI-draft a report narrative for the selected worst-case pair' : 'Select a train and next product first'}
               data-module="ai_assistant" onClick={() => ai.run('AI Draft Report — MACO', () => aiAPI.draftReport({
                 reportType: 'maco',
                 data: { train: selectedTrain?.train_name, previous_api_id: previousApiId, next_product_id: nextProductId, calculation: pairValues },
               }), 'report')}
               style={{ whiteSpace: 'nowrap' }}
             >
               <FaRobot /> AI Draft Report
             </button>
             <button className="btn btn-success btn-sm" onClick={exportExcelTabs}><FaDownload /> Excel Export</button>
             <button className="btn btn-danger btn-sm" onClick={() => setShowPdfModal(true)}><FaDownload /> PDF Export</button>
          </div>
        </div>



        {/* Tab Contents */}
        <div style={{ padding: '20px' }}>

          {/* MATRIX TAB */}
          {activeTab === 'matrix' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--success-color, #10b981)' }}></div>
                    <span style={{ fontSize: 'var(--fs-base)', fontWeight: '600', color: 'var(--text-secondary)' }}>FULL N×N MACO MATRIX</span>
                </div>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                       <label style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', fontWeight: '600' }}>CRITERION:</label>
                       <select className="form-control" style={{ width: '150px', padding: '6px 12px' }} value={matrixCriterion} onChange={e => setMatrixCriterion(e.target.value)}>
                          <option value="governing">Governing (Min)</option>
                          {criteriaEnabled.hbel && <option value="hbel">Health-Base</option>}
                          {criteriaEnabled.dose && <option value="dose">Dose-Based</option>}
                          {criteriaEnabled.ppm && <option value="ppm">10 PPM</option>}
                          {criteriaEnabled.tox && <option value="tox">Toxicological</option>}
                          {/* Always listed (like the others), but disabled with an explicit "not used" label
                              instead of disappearing, so its absence from this line's cleaning is visible rather
                              than silent. It is also never selectable as part of "Governing (Min)" above. */}
                          <option value="detergent" disabled={!criteriaEnabled.detergent}>
                            {criteriaEnabled.detergent ? 'Detergent' : 'Detergent (not used for this line)'}
                          </option>
                       </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                       <label style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', fontWeight: '600' }}>SHOW:</label>
                       <select className="form-control" style={{ width: '110px', padding: '5px 8px', fontSize: 'var(--fs-md)' }} value={matrixUnit} onChange={e => setMatrixUnit(e.target.value)}>
                          <option value="maco">MACO</option>
                          <option value="mar">MAR/cm²</option>
                       </select>
                       {matrixUnit === 'maco' ? (
                         <select className="form-control" style={{ width: '100px', padding: '5px 8px', fontSize: 'var(--fs-md)' }} value={macoDisplayUnit} onChange={e => setMacoDisplayUnit(e.target.value)}>
                           <option value="mg">mg</option>
                           <option value="mcg">µg (mcg)</option>
                         </select>
                       ) : (
                         <select className="form-control" style={{ width: '120px', padding: '5px 8px', fontSize: 'var(--fs-md)' }} value={marDisplayUnit} onChange={e => setMarDisplayUnit(e.target.value)}>
                           <option value="mcg/cm2">µg/cm² (mcg)</option>
                           <option value="mg/cm2">mg/cm²</option>
                         </select>
                       )}
                    </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                   <label style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', fontWeight: '600' }}>MODE:</label>
                   <select className="form-control" style={{ width: '130px', padding: '6px 12px' }} value={matrixMode} onChange={e => setMatrixMode(e.target.value)}>
                      <option value="api">API × API</option>
                      <option value="product">Product × API</option>
                   </select>
                </div>
                </div>
              </div>
              
              <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '16px', backgroundColor: 'var(--surface-light, #f9fafb)', padding: '12px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                 <span><strong>{matrixMode === 'api' ? 'Rows = Next API (B) | Columns = Previous API (A)' : 'Rows = Next Product (B) | Columns = Previous API (A)'}</strong>
                 &nbsp;&nbsp;Red = least value in the matrix (tied values are all red); hover it for the reason, which is also printed under each matrix in the PDF/Excel export. {matrixMode === 'api' ? 'Diagonal cells (same API) are blank. ' : ''}Click any cell to open its Pair Card.</span>
                 <span style={{ fontWeight: 700, fontSize: 'var(--fs-base)', padding: '4px 12px', borderRadius: '999px',
                   background: matrixUnit === 'mar' ? 'var(--success-bg)' : 'var(--info-bg)',
                   color: matrixUnit === 'mar' ? 'var(--success-text)' : 'var(--info-text)',
                   border: matrixUnit === 'mar' ? '1px solid var(--success-border)' : '1px solid var(--info-border)'
                 }}>
                   {matrixUnit === 'mar' ? `Unit: ${marUnitLabel} (MAR)` : `Unit: ${macoUnitLabel} (MACO)`}
                 </span>
              </div>

              {(!selectedTrainId || products.length === 0) ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                     Select an Equipment Train with assigned products to view the Matrix.
                  </div>
              ) : (
                  <FloatingScrollWrapper style={{ borderRadius: '8px', border: '1px solid var(--border-color, #e5e7eb)' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '800px' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid var(--border-color)', backgroundColor: 'var(--surface-light, #f9fafb)', position: 'sticky', left: 0, zIndex: 10, minWidth: '140px', fontSize: 'var(--fs-sm)', fontWeight: '600' }}>Next (B) &darr; / Prev (A) &rarr;</th>
                            {worstCaseData.apisToConsider.map(api => (
                                <th key={api.api_id} style={{ borderBottom: '2px solid var(--border-color)', backgroundColor: 'var(--surface-light, #f9fafb)', padding: '4px', height: '120px', minWidth: '60px', position: 'relative', verticalAlign: 'bottom' }}>
                                   <div style={{ transform: 'rotate(-45deg)', transformOrigin: 'bottom left', whiteSpace: 'nowrap', fontSize: 'var(--fs-sm)', fontWeight: '600', color: 'var(--text-primary)', position: 'absolute', bottom: '8px', left: '12px' }}>{api.api_name}</div>
                                </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                            {matrixMode === 'api' ? (
                              /* API × API mode */
                              worstCaseData.apisToConsider.map(nextApi => (
                                <tr key={nextApi.api_id} style={{ borderBottom: '1px solid var(--border-light, #f3f4f6)' }}>
                                   <td style={{ padding: '8px 12px', fontSize: 'var(--fs-md)', fontWeight: '600', color: 'var(--text-primary)', borderRight: '1px solid var(--border-color)', position: 'sticky', left: 0, backgroundColor: 'var(--surface, #ffffff)', zIndex: 5 }}>
                                     {nextApi.api_name}
                                   </td>
                                   {worstCaseData.apisToConsider.map(prevApi => {
                                       const isDiagonal = String(prevApi.api_id) === String(nextApi.api_id);
                                       if (isDiagonal) {
                                         return <td key={prevApi.api_id} style={{ padding: '6px 4px', textAlign: 'center', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', backgroundColor: 'var(--surface-light, #f5f5f5)', borderRight: '1px solid var(--border-light, #f3f4f6)' }}>—</td>;
                                       }
                                       const res = worstCaseData.apiMatrix[prevApi.api_id]?.[nextApi.api_id];
                                       let val = '-';
                                       if (res) {
                                         const raw = matrixUnit === 'maco' ? res[matrixCriterion] : res[matrixCriterion + 'Mar'];
                                         const converted = matrixUnit === 'maco' ? displayMacoVal(raw) : displayMarVal(raw);
                                         if (converted !== null) val = fmtDisplay(converted, 4) + ' ' + (matrixUnit === 'maco' ? macoUnitLabel : marUnitLabel);
                                       }
                                       const isWorstCell = leastCells.keys.has(`${prevApi.api_id}|${nextApi.api_id}`);
                                       const parentProd = worstCaseData.apiToProduct[String(nextApi.api_id)];
                                       return (
                                          <td 
                                            key={prevApi.api_id} 
                                            {...leastTipHandlers(isWorstCell)}
                                            onClick={() => { if (parentProd) { setPreviousApiId(String(prevApi.api_id)); setNextProductId(String(parentProd.product_id)); setNextApiId(String(nextApi.api_id)); setActiveTab('pairCard'); } }}
                                            style={{ 
                                                cursor: 'pointer', padding: '6px 4px', textAlign: 'center', fontSize: 'var(--fs-sm)', fontFamily: 'monospace',
                                                color: isWorstCell ? 'var(--danger-text, #b91c1c)' : 'var(--text-primary)',
                                                fontWeight: isWorstCell ? 'bold' : 'normal',
                                                backgroundColor: isWorstCell ? 'var(--highlight-bg, #fef2f2)' : 'transparent',
                                                transition: 'background-color 0.15s', borderRight: '1px solid var(--border-light, #f3f4f6)'
                                            }}
                                            onMouseOver={(e) => { if(!isWorstCell) e.currentTarget.style.backgroundColor = 'var(--surface-light, #f9fafb)' }}
                                            onMouseOut={(e) => { if(!isWorstCell) e.currentTarget.style.backgroundColor = 'transparent' }}
                                          >
                                             {val}
                                          </td>
                                       );
                                   })}
                                </tr>
                              ))
                            ) : (
                              /* Product mode (original) */
                              worstCaseData.productsOnTrain.map(prod => (
                                <tr key={prod.product_id} style={{ borderBottom: '1px solid var(--border-light, #f3f4f6)' }}>
                                   <td style={{ padding: '8px 12px', fontSize: 'var(--fs-md)', fontWeight: '600', color: 'var(--text-primary)', borderRight: '1px solid var(--border-color)', position: 'sticky', left: 0, backgroundColor: 'var(--surface, #ffffff)', zIndex: 5 }}>
                                     {prod.product_name}
                                   </td>
                                   {worstCaseData.apisToConsider.map(api => {
                                       const res = worstCaseData.matrix[api.api_id]?.[prod.product_id];
                                       let val = '-';
                                       if (res) {
                                         const raw = matrixUnit === 'maco' ? res[matrixCriterion] : res[matrixCriterion + 'Mar'];
                                         const converted = matrixUnit === 'maco' ? displayMacoVal(raw) : displayMarVal(raw);
                                         if (converted !== null) val = fmtDisplay(converted, 4) + ' ' + (matrixUnit === 'maco' ? macoUnitLabel : marUnitLabel);
                                       }
                                       const isWorstCell = leastCells.keys.has(`${api.api_id}|${prod.product_id}`);
                                       return (
                                          <td 
                                            key={api.api_id} 
                                            {...leastTipHandlers(isWorstCell)}
                                            onClick={() => { setPreviousApiId(String(api.api_id)); setNextProductId(String(prod.product_id)); setNextApiId(String(api.api_id)); setActiveTab('pairCard'); }}
                                            style={{ 
                                                cursor: 'pointer', padding: '6px 4px', textAlign: 'center', fontSize: 'var(--fs-sm)', fontFamily: 'monospace',
                                                color: isWorstCell ? 'var(--danger-text, #b91c1c)' : 'var(--text-primary)',
                                                fontWeight: isWorstCell ? 'bold' : 'normal',
                                                backgroundColor: isWorstCell ? 'var(--highlight-bg, #fef2f2)' : 'transparent',
                                                transition: 'background-color 0.15s', borderRight: '1px solid var(--border-light, #f3f4f6)'
                                            }}
                                            onMouseOver={(e) => { if(!isWorstCell) e.currentTarget.style.backgroundColor = 'var(--surface-light, #f9fafb)' }}
                                            onMouseOut={(e) => { if(!isWorstCell) e.currentTarget.style.backgroundColor = 'transparent' }}
                                          >
                                             {val}
                                          </td>
                                       );
                                   })}
                                </tr>
                              ))
                            )}
                        </tbody>
                    </table>
                  </FloatingScrollWrapper>
              )}
              {leastTip && leastCells.rows.length > 0 && (() => {
                const width = 340;
                const below = leastTip.top < 260;
                const left = Math.min(Math.max(12, leastTip.x - width / 2), window.innerWidth - width - 12);
                return (
                  <div role="tooltip" style={{
                    position: 'fixed', left, width, zIndex: 1500, pointerEvents: 'none',
                    ...(below ? { top: leastTip.bottom + 8 } : { bottom: window.innerHeight - leastTip.top + 8 }),
                    background: 'var(--surface, #fff)', color: 'var(--text-primary)',
                    border: '1px solid var(--border-color, #e5e7eb)', borderTop: '3px solid var(--danger-color, #ff4d4f)',
                    borderRadius: 'var(--radius-lg, 8px)', boxShadow: 'var(--shadow-md)', overflow: 'hidden',
                  }}>
                    <div style={{ padding: '8px 12px', background: 'var(--danger-bg, #fff2f0)', color: 'var(--danger-text, #cf1322)', fontSize: 'var(--fs-sm)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FaExclamationTriangle /> Least {CRITERION_LABELS[matrixCriterion]} in the matrix
                    </div>
                    <div style={{ padding: '8px 12px', display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 12, rowGap: 6, fontSize: 'var(--fs-sm)' }}>
                      {leastCells.rows.map(r => (
                        <React.Fragment key={r.label}>
                          <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{r.label}</span>
                          <span style={{ fontWeight: 600, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{r.value}</span>
                        </React.Fragment>
                      ))}
                    </div>
                    {leastCells.notes.length > 0 && (
                      <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-light, #f3f4f6)', background: 'var(--surface-light, #f9fafb)', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                        {leastCells.notes.map(n => <div key={n}>• {n}</div>)}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* PAIR CARD TAB */}
          {activeTab === 'pairCard' && (() => {
             // Build API×API pairs (excluding diagonal)
             const allPairs = [];
             worstCaseData.apisToConsider.forEach(prevApi => {
               worstCaseData.apisToConsider.forEach(nextApi => {
                 if (String(prevApi.api_id) !== String(nextApi.api_id)) {
                   allPairs.push({ prevApi, nextApi });
                 }
               });
             });
             const curIdx = allPairs.findIndex(p => String(p.prevApi.api_id) === String(previousApiId) && String(p.nextApi.api_id) === String(nextApiId));
             const goPrev = () => { if (curIdx > 0) { setPreviousApiId(String(allPairs[curIdx-1].prevApi.api_id)); setNextApiId(String(allPairs[curIdx-1].nextApi.api_id)); } };
             const goNext = () => { if (curIdx < allPairs.length - 1) { setPreviousApiId(String(allPairs[curIdx+1].prevApi.api_id)); setNextApiId(String(allPairs[curIdx+1].nextApi.api_id)); } };
             const isSameApi = previousApiId && nextApiId && String(previousApiId) === String(nextApiId);
             // Derive pairValues from apiToProduct
             const nextProd = worstCaseData.apiToProduct?.[String(nextApiId)];
             const cardPairValues = !isSameApi && previousApiId && nextProd ? (worstCaseData.matrix[previousApiId]?.[nextProd.product_id] || null) : null;
             const wc = worstCaseData.worstCase?.value === Infinity ? null : worstCaseData.worstCase;
             const draftWc = worstCaseData.draftWorstCase?.value === Infinity ? null : worstCaseData.draftWorstCase;
             const draftDiffersFromApproved = draftWc && (!wc || String(draftWc.api?.api_id) !== String(wc.api?.api_id) || String(draftWc.product?.product_id) !== String(wc.product?.product_id));
             const effectiveSurfaceArea = surfaceAreaCm2 * (1 + trainAreaCompensationPct / 100);
             const wcMacoAdj = wc ? wc.value * purgingFactor : 0;
             const wcMar = wc ? (wcMacoAdj * 1000) / Math.max(1, effectiveSurfaceArea) : 0;
             const wcRinse = wcMacoAdj > 0 ? (wcMacoAdj * 1000 * (swabRecoveryPct / 100)) / Math.max(1, rinsateVolumeMl) : 0;
             const wcSwab = wcMar > 0 ? wcMar * swabAreaCm2 * (swabRecoveryPct / 100) : 0;
             const prevApiName = apis.find(a => String(a.api_id) === String(previousApiId))?.api_name || '—';
             const nextApiName = apis.find(a => String(a.api_id) === String(nextApiId))?.api_name || '—';
             return (
             <div>
                {/* Global Governing MACO Banner */}
                <div style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                   <div style={{ display: 'flex', gap: '8px', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px', marginBottom: '16px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--primary-color)', flexShrink: 0 }}></div>
                      <span style={{ fontSize: 'var(--fs-sm)', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: '0.04em' }}>GLOBAL GOVERNING MACO — WORST CASE (APPROVED PAIRS ONLY × ALL SELECTED CRITERIA)</span>
                   </div>
                   {wc ? (
                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                      <div>
                         <div style={{ fontSize: 'var(--fs-3xl)', fontWeight: '800', color: 'var(--danger-text, #b91c1c)', lineHeight: '1.1' }}>{fmtDisplay(displayMacoVal(wcMacoAdj), 5)} <span style={{ fontSize: 'var(--fs-xl)' }}>{macoUnitLabel}</span></div>
                         {purgingFactor !== 1 && wc && <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '2px' }}>Base MACO: {fmtDisplay(displayMacoVal(wc.value), 5)} {macoUnitLabel} × PF {purgingFactor}</div>}
                         <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '4px' }}>Worst-case across all {allPairs.length} product pairs — {wc.api?.api_name || '—'} → {wc.product?.product_name || '—'}</div>
                         {currentGoverning && String(currentGoverning.previous_api_id) === String(wc.api?.api_id) && String(currentGoverning.next_product_id) === String(wc.product?.product_id) ? (
                           <div style={{ marginTop: '8px', fontSize: 'var(--fs-sm)', padding: '6px 10px', borderRadius: '6px', background: 'var(--success-bg)', color: 'var(--success-text)', display: 'inline-block' }}>
                             ✔ Signed as governing by {currentGoverning.signed_by_user || 'user'} on {new Date(currentGoverning.signed_at).toLocaleString()}
                           </div>
                         ) : (
                           <div style={{ marginTop: '8px' }}>
                             <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: 'var(--fs-sm)' }}
                               onClick={() => { setGoverningWcPair({ api_id: wc.api?.api_id, product_id: wc.product?.product_id }); setGoverningESignData({ password: '', comment: '' }); setShowGoverningESign(true); }}>
                               Sign &amp; Finalize Governing MACO
                             </button>
                             {currentGoverning && (
                               <span style={{ marginLeft: '10px', fontSize: 'var(--fs-xs)', color: 'var(--warning-text)' }}>
                                 ⚠ Signed governing record on file is {currentGoverning.previous_api_name || '—'} → {currentGoverning.next_product_name || '—'} — resign to update it
                               </span>
                             )}
                           </div>
                         )}
                         {draftDiffersFromApproved && (
                           <div style={{ marginTop: '8px', fontSize: 'var(--fs-sm)', padding: '6px 10px', borderRadius: '6px', background: 'var(--warning-bg)', color: 'var(--warning-text)', display: 'inline-block' }}>
                             ⚠ Including draft/pending items, the worst case would be {draftWc.api?.api_name || '—'} → {draftWc.product?.product_name || '—'} ({fmtDisplay(displayMacoVal(draftWc.value * purgingFactor), 5)} {macoUnitLabel}) — not official until approved.
                           </div>
                         )}
                      </div>
                      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                         <div style={{ padding: '10px 16px', border: '1px solid var(--border-color)', borderRadius: '8px', textAlign: 'center', backgroundColor: 'var(--surface)' }}>
                           <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>MAR/cm²</div>
                           <div style={{ fontSize: 'var(--fs-xl)', fontWeight: '700', color: 'var(--success-color, #059669)' }}>{fmtDisplay(displayMarVal(wcMar), 6)} <span style={{ fontSize: 'var(--fs-sm)' }}>{marUnitLabel}</span></div>
                         </div>
                         <div style={{ padding: '10px 16px', border: '1px solid var(--border-color)', borderRadius: '8px', textAlign: 'center', backgroundColor: 'var(--surface)' }}>
                           <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Rinse Limit ({(rinsateVolumeMl/1000).toFixed(1)}L, R={swabRecoveryPct}%)</div>
                           <div style={{ fontSize: 'var(--fs-xl)', fontWeight: '700', color: 'var(--primary-color, #2563eb)' }}>{wcRinse > 0 ? fmt(wcRinse, 4) : '—'} <span style={{ fontSize: 'var(--fs-sm)' }}>µg/mL</span></div>
                         </div>
                         <div style={{ padding: '10px 16px', border: '1px solid var(--border-color)', borderRadius: '8px', textAlign: 'center', backgroundColor: 'var(--surface)' }}>
                           <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Swab Limit ({swabAreaCm2}cm², R={swabRecoveryPct}%)</div>
                           <div style={{ fontSize: 'var(--fs-xl)', fontWeight: '700', color: 'var(--warning-text, #92400e)' }}>{wcSwab > 0 ? fmt(wcSwab, 4) : '—'} <span style={{ fontSize: 'var(--fs-sm)' }}>µg/swab</span></div>
                         </div>
                      </div>
                   </div>
                   ) : (
                     <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>
                       No approved API/Product pair yet on this train — nothing to validate against until at least one full pair (API + Product + all its APIs) is Approved.
                       {draftWc && (
                         <div style={{ marginTop: '8px', fontSize: 'var(--fs-sm)', padding: '6px 10px', borderRadius: '6px', background: 'var(--warning-bg)', color: 'var(--warning-text)', display: 'inline-block' }}>
                           ⚠ Draft preview: {draftWc.api?.api_name || '—'} → {draftWc.product?.product_name || '—'} ({fmtDisplay(displayMacoVal(draftWc.value * purgingFactor), 5)} {macoUnitLabel}) — provisional only.
                         </div>
                       )}
                     </div>
                   )}
                </div>

                {/* SELECT PRODUCT PAIR with Prev/Next */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                   <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--success-color)' }}></div>
                      <span style={{ fontSize: 'var(--fs-base)', fontWeight: '600', color: 'var(--text-secondary)' }}>SELECT PRODUCT PAIR</span>
                   </div>
                   <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <button className="btn btn-outline" style={{ padding: '4px 12px', fontSize: 'var(--fs-md)' }} disabled={curIdx <= 0} onClick={goPrev}>◄ Prev</button>
                      <span style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', fontWeight: '600' }}>{curIdx >= 0 ? curIdx + 1 : '—'} / {allPairs.length}</span>
                      <button className="btn btn-outline" style={{ padding: '4px 12px', fontSize: 'var(--fs-md)' }} disabled={curIdx >= allPairs.length - 1} onClick={goNext}>Next ►</button>
                   </div>
                </div>

                {/* Dropdowns row — both show APIs */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' }}>
                    <div>
                       <label style={{ display: 'block', fontSize: 'var(--fs-sm)', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '4px' }}>Previous API (A)</label>
                       <select className="form-control" value={previousApiId} onChange={(e) => setPreviousApiId(e.target.value)}>
                          {worstCaseData.apisToConsider.map((a) => <option key={a.api_id} value={a.api_id}>{a.api_name}</option>)}
                       </select>
                    </div>
                    <div>
                       <label style={{ display: 'block', fontSize: 'var(--fs-sm)', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '4px' }}>Next API (B)</label>
                       <select className="form-control" value={nextApiId} onChange={(e) => setNextApiId(e.target.value)}>
                          {worstCaseData.apisToConsider.map((a) => <option key={a.api_id} value={a.api_id}>{a.api_name}</option>)}
                       </select>
                    </div>
                </div>

                {/* Styled pill cards */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                   <div style={{ padding: '10px 24px', borderRadius: '8px', background: 'linear-gradient(135deg, var(--primary-color, #2563eb), var(--primary-dark, #1d4ed8))', textAlign: 'center', minWidth: '180px', boxShadow: '0 2px 8px rgba(37,99,235,0.25)' }}>
                      <div style={{ fontSize: 'var(--fs-2xs)', color: '#bfdbfe', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>PREVIOUS (A)</div>
                      <div style={{ fontSize: 'var(--fs-base)', fontWeight: '700', color: '#ffffff' }}>{prevApiName}</div>
                   </div>
                   <div style={{ fontSize: 'var(--fs-xl)', color: 'var(--primary-color, #2563eb)', fontWeight: '700' }}>→</div>
                   <div style={{ padding: '10px 24px', borderRadius: '8px', background: 'linear-gradient(135deg, var(--primary-color, #2563eb), var(--primary-dark, #1d4ed8))', textAlign: 'center', minWidth: '180px', boxShadow: '0 2px 8px rgba(37,99,235,0.25)' }}>
                      <div style={{ fontSize: 'var(--fs-2xs)', color: '#bfdbfe', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>NEXT (B)</div>
                      <div style={{ fontSize: 'var(--fs-base)', fontWeight: '700', color: '#ffffff' }}>{nextApiName}</div>
                   </div>
                </div>

                {isSameApi ? (
                    <div style={{ backgroundColor: 'var(--warning-bg, #fffbeb)', padding: '16px', borderRadius: '8px', textAlign: 'center', color: 'var(--warning-text, #92400e)', border: '1px solid var(--warning-border, #fde68a)' }}>
                        <strong>Invalid pair</strong> — Same API selected on both sides. Select different APIs for Previous (A) and Next (B).
                    </div>
                ) : !cardPairValues ? (
                    <div style={{ backgroundColor: 'var(--warning-bg, #fffbeb)', padding: '16px', borderRadius: '8px', textAlign: 'center', color: 'var(--warning-text, #92400e)' }}>
                        Select valid Previous API and Next API to view pair details.
                    </div>
                ) : (
                    <div>
                       <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                          <button className="btn btn-primary" onClick={() => {
                             const content = document.getElementById('pair-card-print-content');
                             if (!content) return toast.error('Print content not found');
                             openPrintPreview({
      reportKey: 'maco',
                                contentHTML: content.innerHTML,
                                title: 'MACO Pair Calculation Details',
                                documentName: `Pair Card: ${prevApiName} to ${nextApiName}`,
                                formatNo: reportSettings?.report_format_no_maco || '',
                                fitOnePage: true
                             });
                          }}><FaPrint /> Print Pair Details</button>
                       </div>
                       <div id="pair-card-print-content">
                       {/* Governing Pair Card wrapper */}
                       <div style={{ border: '2px solid var(--danger-border, #fca5a5)', borderRadius: '12px', padding: '24px', backgroundColor: 'var(--surface-light, #fcfcfc)', marginBottom: '24px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
                             <div>
                                <div style={{ fontSize: 'var(--fs-md)', color: 'var(--danger-text, #b91c1c)', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px', display:'flex', alignItems:'center', gap:'8px' }}>
                                  GOVERNING MACO — THIS PAIR
                                  {cardPairValues.isProvisional && <span style={{fontSize:'var(--fs-xs)',padding:'2px 7px',borderRadius:'999px',background:'var(--warning-bg)',color:'var(--warning-text)'}}>Draft scenario · provisional</span>}
                                  <select style={{ fontSize:'var(--fs-sm)', padding:'1px 4px', border:'1px solid var(--border-color)', borderRadius:'4px', background:'var(--card-bg)', fontWeight:'normal', textTransform:'none' }} value={macoDisplayUnit} onChange={e => setMacoDisplayUnit(e.target.value)}>
                                    <option value="mg">mg</option>
                                    <option value="mcg">µg (mcg)</option>
                                  </select>
                                </div>
                                <div style={{ fontSize: 'var(--fs-3xl)', fontWeight: '800', color: 'var(--danger-text, #991b1b)', lineHeight: '1' }}>
                                   {fmtDisplay(displayMacoVal(cardPairValues.governing), 5)} <span style={{ fontSize: 'var(--fs-xl)' }}>{macoUnitLabel}</span>
                                </div>
                                <div style={{ fontSize: 'var(--fs-base)', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                   Governing criterion | SA: {effectiveSurfaceAreaCm2.toFixed(1)} cm²
                                   {trainSurfaceAreaCm2 ? ' (train contact)' : ' (global setting)'}
                                </div>
                             </div>
                             <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: '4px' }}>MAR/cm²
                                  <select style={{ marginLeft:'6px', fontSize:'var(--fs-sm)', padding:'1px 4px', border:'1px solid var(--border-color)', borderRadius:'4px', background:'var(--card-bg)' }} value={marDisplayUnit} onChange={e => setMarDisplayUnit(e.target.value)}>
                                    <option value="mcg/cm2">µg/cm²</option>
                                    <option value="mg/cm2">mg/cm²</option>
                                  </select>
                                </div>
                                <div style={{ fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--success-color, #059669)', marginBottom: '12px' }}>
                                  {fmtDisplay(displayMarVal(cardPairValues.governingMar * purgingFactor), 6)} {marUnitLabel}
                                </div>
                                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: '4px' }}>Rinse Limit ({(rinsateVolumeMl/1000).toFixed(1)}L, R={swabRecoveryPct}%)</div>
                                <div style={{ fontSize: 'var(--fs-lg)', fontWeight: '600', color: 'var(--primary-color, #2563eb)' }}>{fmt((cardPairValues.governing * purgingFactor * 1000 * swabRecoveryPct / 100) / rinsateVolumeMl, 4)} mcg/mL</div>
                             </div>
                          </div>
                       </div>

                       {/* The 5 Criteria bottom cards */}
                       <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                          {[
                            { title:'HEALTH-BASE (ADE/PDE)',    formula:'MACO = (PDE × MBS) / (SF × LDD)',      sf:hbelSafetyFactor,   maco:cardPairValues.hbel,      mar:cardPairValues.hbelMar,      en:criteriaEnabled.hbel },
                            { title:activeLineRoute?`DOSE-BASED (${ROUTE_DISPLAY[activeLineRoute]||activeLineRoute})`:'DOSE-BASED (0.1%)', formula:'MACO = (TDD × MBS) / (SF × LDD)', sf:effectiveDoseSF, maco:cardPairValues.dose, mar:cardPairValues.doseMar, en:criteriaEnabled.dose },
                            { title:'10 PPM CRITERION',         formula:'MACO = ppm × MBS(kg)',                  sf:ppmLimit,           maco:cardPairValues.ppm,       mar:cardPairValues.ppmMar,       en:criteriaEnabled.ppm },
                            { title:'TOXICOLOGICAL (NOEL)',     formula:'NOEL = LD50 × BW × 0.0005; MACO = (NOEL × MBS) / (SF × LDD)',     sf:toxSafetyFactor,    maco:cardPairValues.tox,       mar:cardPairValues.toxMar,       en:criteriaEnabled.tox },
                            // Detergent is always listed (never hidden) so its status is explicit either way, but it
                            // can never be the "GOVERNING" card — it's a separate contaminant from the product/API
                            // residue criteria above and is tracked as its own independent limit (see
                            // Acceptance Criteria's Detergent MAR), never allowed to silently replace the product MAR.
                            { title:'DETERGENT TOXICITY', formula:'MACO = (ADE_det × MBS) / LDD', sf:detergentSafetyFactor, maco:cardPairValues.detergent, mar:cardPairValues.detergentMar, en:criteriaEnabled.detergent, isDetergent: true, notUsedMessage: !cardPairValues.detergentActive ? 'No detergent used for this line — enable this criterion and set a detergent LD50 in Settings to calculate a limit.' : null },
                          ].map((c,i) => (
                            <CriteriaCard key={i}
                              title={c.title} formula={c.formula} safetyFactor={c.sf}
                              macoValue={c.maco} marValue={c.mar} enabled={c.en}
                              isGoverning={!c.isDetergent && cardPairValues.governing === c.maco && c.en && c.maco > 0}
                              macoUnitLabel={macoUnitLabel} marUnitLabel={marUnitLabel}
                              displayMacoVal={displayMacoVal} displayMarVal={displayMarVal}
                              notUsedMessage={c.notUsedMessage}
                            />
                          ))}
                       </div>
                       </div>
                    </div>
                )}
          </div>
          )})()}

          {/* SUMMARY TAB */}
          {activeTab === 'summary' && (
             <div>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                  <button className="btn btn-primary" onClick={() => {
                    const content = document.getElementById('summary-print-content');
                    if (!content) return;
                    openPrintPreview({
      reportKey: 'maco',
                      contentHTML: content.innerHTML,
                      title: 'MACO Summary Report',
                      documentName: `Summary: ${selectedTrain?.train_code} - ${selectedTrain?.train_name}`,
                      formatNo: reportSettings?.report_format_no_maco || '',
                      fitOnePage: false
                    });
                  }}><FaPrint /> Print Summary</button>
                </div>

                <div id="summary-print-content">
                <div style={{
                    border: '1px solid var(--border-color, #e5e7eb)',
                    borderRadius: '12px',
                    padding: '24px',
                    backgroundColor: 'var(--surface-light, #f9fafb)',
                    marginBottom: '24px'
                }}>
                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px', marginBottom: '16px' }}>
                     <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                         <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--primary-color)' }}></div>
                         <span style={{ fontSize: 'var(--fs-base)', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>GLOBAL GOVERNING MACO — WORST CASE (ALL PAIRS)</span>
                     </div>
                   </div>
                   
                   <div style={{ display: 'flex', flexWrap: 'wrap', gap: '30px', alignItems: 'flex-start' }}>
                      <div style={{ flex: '1 1 300px' }}>
                         <div style={{ fontSize: 'var(--fs-3xl)', fontWeight: '800', color: 'var(--danger-text, #b91c1c)', lineHeight: '1' }}>
                            {worstCaseData.worstCase && worstCaseData.worstCase.value !== Infinity ? fmtDisplay(displayMacoVal(worstCaseData.worstCase.value), 5) : 'N/A'} <span style={{ fontSize: 'var(--fs-2xl)' }}>{macoUnitLabel}</span>
                         </div>
                         <div style={{ fontSize: 'var(--fs-lg)', color: 'var(--text-secondary)', marginTop: '8px' }}>
                            Worst-case across all {worstCaseData.productsOnTrain.length * worstCaseData.apisToConsider.length} product pairs on train {selectedTrain?.train_code}
                         </div>
                      </div>

                      {worstCaseData.worstCase && worstCaseData.worstCase.value !== Infinity && (
                          <div style={{ flex: '2 1 300px', display: 'flex', gap: '20px' }}>
                             <div style={{ padding: '12px 20px', backgroundColor: 'var(--surface)', border: '1px solid var(--border-color)', borderRadius: '8px', flex: 1, textAlign: 'center' }}>
                                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: '8px' }}>MAR/cm²</div>
                                <div style={{ fontSize: 'var(--fs-xl)', fontWeight: '700', color: 'var(--success-color, #059669)' }}>{fmtDisplay(displayMarVal(worstCaseData.worstCase.mar), 6)} <span style={{ fontSize: 'var(--fs-md)' }}>{marUnitLabel}</span></div>
                             </div>
                             <div style={{ padding: '12px 20px', backgroundColor: 'var(--surface)', border: '1px solid var(--border-color)', borderRadius: '8px', flex: 1, textAlign: 'center' }}>
                                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: '8px' }}>Worst Pair</div>
                                <div style={{ fontSize: 'var(--fs-base)', fontWeight: '600', color: 'var(--primary-color, var(--primary-color, #2563eb))' }}>{worstCaseData.worstCase.api?.api_name}</div>
                                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>→ {worstCaseData.worstCase.product?.product_name}</div>
                             </div>
                          </div>
                      )}
                   </div>
                </div>

                <div className="card">
                   <div className="card-header"><h3 className="card-title" style={{ fontSize: 'var(--fs-xl)' }}>Train & Settings Summary</h3></div>
                   <div style={{ padding: '20px' }}>
                      <FloatingScrollWrapper>
                      <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
                         <tbody>
                            <tr><th style={{ padding: '12px', borderBottom: '1px solid var(--border-color)', width: '40%' }}>Equipment Train</th><td style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>{selectedTrain?.train_id ? (<span className="record-link" onClick={() => navigate('/equipment-trains', { state: { openViewTrainId: selectedTrain.train_id } })} title="View train">{selectedTrain.train_code} - {selectedTrain.train_name}</span>) : `${selectedTrain?.train_code} - ${selectedTrain?.train_name}`}</td></tr>
                            <tr><th style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>Surface Area</th><td style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>{surfaceAreaCm2} cm²</td></tr>
                            <tr><th style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>HBEL Safety Factor</th><td style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>{hbelSafetyFactor}</td></tr>
                            <tr><th style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>Dose Safety Factor</th><td style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>{effectiveDoseSF}{activeLineRoute ? ` (${ROUTE_DISPLAY[activeLineRoute] || activeLineRoute} — auto from section)` : ''}</td></tr>
                            <tr><th style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>10 PPM Limit</th><td style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>{ppmLimit}</td></tr>
                            <tr><th style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>Tox Safety Factor</th><td style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>{toxSafetyFactor}</td></tr>
                         </tbody>
                      </table>
                      </FloatingScrollWrapper>
                   </div>
                </div>
                </div>{/* end summary-print-content */}

             </div>
          )}

          {/* DETERGENTS TAB */}
          {activeTab === 'detergents' && (
            <div style={{ padding: '8px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <span style={{ fontSize: 'var(--fs-base)', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Detergent Master
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-outline btn-sm" onClick={downloadDetImportTemplate}><FaDownload /> Template</button>
                  <input ref={detImportInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleDetImportFile} />
                  <button className="btn btn-outline btn-sm" onClick={() => detImportInputRef.current?.click()}><FaFileUpload /> Import</button>
                  <button className="btn btn-primary btn-sm" onClick={() => { setDetModalForm({ id: null, detergent_name: '', category: '', concentration: '', notes: '', ld50_value: '' }); setDetModalOpen(true); }}>
                    + Add Detergent
                  </button>
                </div>
              </div>
              {detImportResult && (
                <div className="alert alert-info" style={{ marginBottom: '12px', fontSize: 'var(--fs-sm)' }}>
                  Import complete: {detImportResult.created} created, {detImportResult.results.filter(r => r.status === 'skipped').length} skipped, {detImportResult.results.filter(r => r.status === 'error').length} failed (of {detImportResult.total} rows).
                  {detImportResult.results.some(r => r.status !== 'created') && (
                    <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                      {detImportResult.results.filter(r => r.status !== 'created').map((r, i) => (
                        <li key={i}>Row {r.row}{r.detergent_name ? ` (${r.detergent_name})` : ''}: {r.message}</li>
                      ))}
                    </ul>
                  )}
                  <button className="btn btn-sm btn-outline" style={{ marginTop: '8px' }} onClick={() => setDetImportResult(null)}>Dismiss</button>
                </div>
              )}
              {detergentList.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-base)' }}>No detergents defined yet. Add one above.</p>
              ) : (
                <FloatingScrollWrapper>
                <table style={{ width: '100%', fontSize: 'var(--fs-base)', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Category</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Concentration</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>LD50 (mg/kg)</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Notes</th>
                      <th style={{ padding: '6px 10px', borderBottom: '2px solid var(--border-color)', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDetergents.map(d => (
                      <tr key={d.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '6px 10px', fontWeight: 600 }}>{d.detergent_name}</td>
                        <td style={{ padding: '6px 10px', color: 'var(--text-secondary)' }}>{d.category || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>{d.concentration || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>{d.ld50_value || <span style={{ color: 'var(--warning-text)' }}>Not set</span>}</td>
                        <td style={{ padding: '6px 10px', color: 'var(--text-secondary)' }}>{d.notes || '—'}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <button className="btn btn-sm btn-info" style={{ padding: '3px 7px' }}
                              onClick={() => { setDetModalForm({ id: d.id, detergent_name: d.detergent_name, category: d.category || '', concentration: d.concentration || '', notes: d.notes || '', ld50_value: d.ld50_value || '' }); setDetModalOpen(true); }}>
                              Edit
                            </button>
                            <button className="btn btn-sm btn-danger-outline" style={{ padding: '3px 7px' }}
                              onClick={async () => {
                                if (!window.confirm(`Deactivate "${d.detergent_name}"?`)) return;
                                try { await detergentMasterAPI.delete(d.id); setDetergentList(prev => prev.filter(x => x.id !== d.id)); toast.success('Deactivated.'); }
                                catch { toast.error('Deactivate failed.'); }
                              }}>
                              Deactivate
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </FloatingScrollWrapper>
              )}
              {detergentList.length > 0 && (
                <TablePagination total={detergentList.length} page={detergentPage} pageSize={detergentPageSize} onPageChange={setDetergentPage} onPageSizeChange={(n) => { setDetergentPageSize(n); setDetergentPage(1); }} itemLabel="detergents" />
              )}

              {/* Add / Edit Detergent Mini-Modal */}
              {detModalOpen && (
                <Modal isOpen size="sm" title={`${detModalForm.id ? 'Edit' : 'Add'} Detergent`} onClose={() => setDetModalOpen(false)}>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {[['Name *', 'detergent_name', 'e.g. Caustic Soda'], ['Category', 'category', 'e.g. Alkaline'], ['Concentration', 'concentration', 'e.g. 2% w/v']].map(([label, field, ph]) => (
                        <div key={field}>
                          <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '3px', display: 'block' }}>{label}</label>
                          <input className="form-control" placeholder={ph} value={detModalForm[field]} onChange={e => setDetModalForm(f => ({ ...f, [field]: e.target.value }))} />
                        </div>
                      ))}
                      <div>
                        <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '3px', display: 'block' }}>LD50 (mg/kg)</label>
                        <input type="number" min="0" className="form-control" placeholder="e.g. 2000" value={detModalForm.ld50_value}
                          onChange={e => setDetModalForm(f => ({ ...f, ld50_value: e.target.value }))} />
                        <small className="text-muted" style={{ display: 'block', marginTop: '3px' }}>
                          Used by MACO's Detergent Toxicity criterion for any train/line this detergent is configured on in Cleaning Config. Leave blank to fall back to the global LD50 in MACO Settings.
                        </small>
                      </div>
                      <div>
                        <label style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '3px', display: 'block' }}>Notes</label>
                        <textarea className="form-control" rows={2} value={detModalForm.notes} onChange={e => setDetModalForm(f => ({ ...f, notes: e.target.value }))} style={{ resize: 'vertical' }} />
                      </div>
                    </div>
                    <ModalFooter>
                      <button className="btn btn-outline" onClick={() => setDetModalOpen(false)}>Cancel</button>
                      <button className="btn btn-primary" disabled={detSaving} onClick={async () => {
                        if (!detModalForm.detergent_name.trim()) { toast.error('Name required'); return; }
                        setDetSaving(true);
                        try {
                          if (detModalForm.id) {
                            const r = await detergentMasterAPI.update(detModalForm.id, detModalForm);
                            setDetergentList(prev => prev.map(x => x.id === detModalForm.id ? r.data.data : x));
                          } else {
                            const r = await detergentMasterAPI.create(detModalForm);
                            setDetergentList(prev => [...prev, r.data.data].sort((a, b) => a.detergent_name.localeCompare(b.detergent_name)));
                          }
                          toast.success('Saved.');
                          setDetModalOpen(false);
                        } catch (e) { toast.error(e?.response?.data?.message || 'Save failed'); }
                        finally { setDetSaving(false); }
                      }}>
                        {detSaving ? 'Saving…' : 'Save'}
                      </button>
                    </ModalFooter>
                </Modal>
              )}

              {/* Bulk Import Preview */}
              {detImportPreview && (
                <Modal isOpen size="lg" title={`Import ${detImportPreview.rows.length} Detergent(s) — ${detImportPreview.fileName}`} onClose={() => setDetImportPreview(null)}>
                  <FloatingScrollWrapper>
                    <table style={{ width: '100%', fontSize: 'var(--fs-sm)', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Name</th>
                          <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Category</th>
                          <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Concentration</th>
                          <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>LD50 (mg/kg)</th>
                          <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detImportPreview.rows.map((row, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <td style={{ padding: '6px 10px' }}>{row.detergent_name || <span style={{ color: 'var(--danger-text)' }}>Missing</span>}</td>
                            <td style={{ padding: '6px 10px' }}>{row.category || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>{row.concentration || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>{row.ld50_value || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>{row.notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </FloatingScrollWrapper>
                  <ModalFooter>
                    <button className="btn btn-outline" onClick={() => setDetImportPreview(null)}>Cancel</button>
                    <button className="btn btn-primary" disabled={detImporting} onClick={confirmDetImport}>
                      {detImporting ? 'Importing…' : `Import ${detImportPreview.rows.length} Row(s)`}
                    </button>
                  </ModalFooter>
                </Modal>
              )}
            </div>
          )}

          {/* SETTINGS TAB */}
          {activeTab === 'settings' && (
            <div>
              {/* Header row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--primary-color)' }}></div>
                    <span style={{ fontSize: 'var(--fs-base)', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>MACO CALCULATION SETTINGS</span>
                  </div>
                  <p style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', margin: 0 }}>
                    Settings apply to all MACO calculations on this train. {canEditSettings ? 'Save to persist changes.' : 'View only — contact an Admin to change.'}
                  </p>
                </div>
                {canEditSettings && (
                  <button
                    className={`btn btn-primary btn-sm${settingsDirty ? '' : ' btn-outline'}`}
                    onClick={saveSettings}
                    disabled={!settingsDirty || settingsSaving}
                  >
                    {settingsSaving ? 'Saving…' : settingsDirty ? '💾 Save Settings' : 'No Changes'}
                  </button>
                )}
              </div>

              {!canEditSettings && (
                <div style={{ padding: '12px 16px', borderRadius: '8px', backgroundColor: 'var(--info-bg, #dbeafe)', border: '1px solid var(--info-border, #93c5fd)', color: 'var(--info-text, #1e40af)', fontSize: 'var(--fs-base)', marginBottom: '20px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span>🔒</span>
                  <span>You have <strong>view-only</strong> access. These settings are managed by an Admin or Super Admin.</span>
                </div>
              )}

              {/* Safety Factors */}
              <div className="card" style={{ marginBottom: '20px' }}>
                <div className="card-header">
                  <h3 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Safety Factors &amp; Limits</h3>
                </div>
                <div style={{ padding: '20px' }}>
                  {/* Flat fields grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    {[
                      { label: 'HBEL Safety Factor',        desc: 'Divisor for Health-Base (ADE/PDE) MACO',                                                                                        value: hbelSafetyFactor,        setter: v => { setHbelSafetyFactor(Math.max(0.000001, v)); setSettingsDirty(true); }, step: 0.001 },
                      { label: '10 PPM Limit',               desc: 'ppm allowed in subsequent product batch',                                                                                       value: ppmLimit,                setter: v => { setPpmLimit(Math.max(0, v)); setSettingsDirty(true); },                 step: 1     },
                      { label: 'Tox Safety Factor',          desc: 'Divisor for Toxicological (LD50/NOEL) MACO',                                                                                   value: toxSafetyFactor,         setter: v => { setToxSafetyFactor(Math.max(0.000001, v)); setSettingsDirty(true); },   step: 1     },
                      { label: 'Inter-Product SF (APIC)',    desc: 'Interaction safety factor per APIC. Default 1 = no known risk. Applied: MACO = (HBEL × MBS × PF) / (TDD × SF).',             value: macoSF,                  setter: v => { setMacoSF(Math.max(0.01, v)); setSettingsDirty(true); },               step: 0.1   },
                      { label: 'Purging Factor (PF)',        desc: 'Multiplier when validated purge reduces carryover. Default 1.0 = no purge benefit.',                                           value: purgingFactor,           setter: v => { setPurgingFactor(Math.max(1, v)); setSettingsDirty(true); },            step: 0.1   },
                      { label: 'Train Area Compensation (%)',desc: 'Added to nominal surface area for inaccessible zones. Effective Area = Nominal × (1 + %/100). Higher % → stricter MAR/cm².', value: trainAreaCompensationPct,setter: v => { setTrainAreaCompensationPct(Math.max(0, v)); setSettingsDirty(true); }, step: 0.1   },
                    ].map(f => (
                      <div key={f.label} className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: 'var(--fs-md)', fontWeight: '600' }}>{f.label}</label>
                        <input type="number" className="form-control" value={f.value} disabled={!canEditSettings} step={f.step}
                          onChange={e => f.setter(toNum(e.target.value, f.value))} style={{ opacity: canEditSettings ? 1 : 0.7 }} />
                        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '4px' }}>{f.desc}</div>
                      </div>
                    ))}
                  </div>

                  {/* Dose Safety Factor by Route */}
                  <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '18px' }}>
                    <div style={{ fontSize: 'var(--fs-base)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
                      Dose Safety Factor by Route of Administration
                      <span style={{ marginLeft: '8px', fontSize: 'var(--fs-sm)', fontWeight: 400, color: 'var(--text-secondary)' }}>
                        — used in Dose-Based MACO = (TDD × MBS) / (SF_route × LDD)
                      </span>
                    </div>
                    {activeLineRoute && (
                      <div style={{ fontSize: 'var(--fs-md)', color: 'var(--success-color, #10b981)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>Active line route:</span>
                        <strong>{ROUTE_DISPLAY[activeLineRoute] || activeLineRoute}</strong>
                        <span>→ SF = {effectiveDoseSF} (auto-applied to calculations)</span>
                      </div>
                    )}
                    <table style={{ width: '100%', tableLayout: 'fixed' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '28%' }}>Route of Administration</th>
                            <th style={{ width: '130px' }}>Safety Factor</th>
                            <th>Notes (per ISPE / APIC / EMA)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { key: 'oral',        label: 'Oral',                   note: 'Default 1000. 0.001× min therapeutic dose per oral route.' },
                            { key: 'topical',     label: 'Topical / Dermal',        note: 'Default 1000. Systemic absorption may be low; verify per product.' },
                            { key: 'injectable',  label: 'Injectable / Parenteral', note: 'Default 100. 100% bioavailability → stricter limit. Ref: EMA/CHMP.' },
                            { key: 'inhalation',  label: 'Inhalation',              note: 'Default 100. Based on inhaled exposure limits; product-specific.' },
                            { key: 'ophthalmic',  label: 'Ophthalmic',              note: 'Default 100. Low systemic risk but highly sensitive tissue.' },
                            { key: 'transdermal', label: 'Transdermal',             note: 'Default 1000. Adjust based on validated skin permeation data.' },
                          ].map(r => {
                            const isActive = r.key === activeLineRoute;
                            const isNavHighlight = r.key === navHighlightRoute && !isActive;
                            let rowStyle = {};
                            if (isActive) rowStyle = { background: 'color-mix(in srgb, var(--success-color, #10b981) 8%, var(--card-bg))', outline: '2px solid var(--success-color, #10b981)', outlineOffset: '-2px' };
                            else if (isNavHighlight) rowStyle = { background: 'color-mix(in srgb, var(--warning-color) 10%, var(--card-bg))', outline: '2px solid var(--warning-color)', outlineOffset: '-2px', animation: 'pulse 1s ease 3' };
                            return (
                              <tr key={r.key} ref={(isActive || isNavHighlight) ? routeHighlightRef : null} style={rowStyle}>
                                <td style={{ fontWeight: (isActive || isNavHighlight) ? 700 : 600 }}>
                                  {r.label}
                                  {isActive && <span style={{ marginLeft: '8px', fontSize: 'var(--fs-xs)', padding: '2px 6px', borderRadius: '10px', background: 'var(--success-color, #10b981)', color: '#fff', fontWeight: 600 }}>Active Section</span>}
                                  {isNavHighlight && <span style={{ marginLeft: '8px', fontSize: 'var(--fs-xs)', padding: '2px 6px', borderRadius: '10px', background: 'var(--warning-color)', color: '#fff', fontWeight: 600 }}>← From Product</span>}
                                </td>
                                <td>
                                  <input
                                    type="number" min="0.001" step="10"
                                    value={doseSFByRoute[r.key] ?? DEFAULT_DOSE_SF_BY_ROUTE[r.key]}
                                    disabled={!canEditSettings}
                                    onChange={e => { setDoseSFByRoute(prev => ({ ...prev, [r.key]: Math.max(0.001, toNum(e.target.value, DEFAULT_DOSE_SF_BY_ROUTE[r.key])) })); setSettingsDirty(true); }}
                                    style={{ width: '100px', padding: '4px 6px', borderRadius: '6px', border: `1px solid ${isActive ? 'var(--success-color, #10b981)' : 'var(--border-color)'}`, background: 'var(--card-bg)', color: 'var(--text-primary)', opacity: canEditSettings ? 1 : 0.7, fontWeight: isActive ? 700 : 400 }}
                                  />
                                </td>
                                <td style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>{r.note}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Criteria Selection */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Active MACO Criteria</h3>
                </div>
                <div style={{ padding: '20px' }}>
                  <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                    All enabled criteria are calculated; the least (most stringent) value becomes the <strong>Governing MACO</strong>.
                    {!canEditSettings && <span style={{ marginLeft: '8px', color: 'var(--info-text, #1e40af)' }}>Read-only view.</span>}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
                    {[
                      { key: 'hbel',      title: 'Health-Base (ADE/PDE)',    formula: 'MACO = (PDE × MBS) / (SF × LDD)', desc: 'Toxicologically derived safe daily carryover per ICH Q3C.', color: '#a78bfa' },
                      { key: 'dose',      title: 'Dose-Based (0.1%)',         formula: 'MACO = (TDD × MBS) / (SF × LDD)', desc: 'Carryover ≤ 0.1% of minimum therapeutic dose.',            color: '#00d4aa' },
                      { key: 'ppm',       title: '10 PPM Criterion',          formula: 'MACO = ppm × MBS(kg)',            desc: 'No more than 10 ppm in any subsequent product.',            color: 'var(--primary-color, #3b82f6)' },
                      { key: 'tox',       title: 'Toxicological (LD50/NOEL)', formula: 'NOEL = LD50 × BW × 0.0005; MACO = (NOEL × MBS) / (SF × LDD)', desc: 'LD50 (mg/kg) scaled to a 70 kg adult and converted to a NOEL (Layton factor 0.0005).',           color: '#f87171' },
                      // Hidden entirely when no equipment in the current plant/line scope is actually
                      // cleaned with a detergent (per Cleaning Config) — unless it was already manually
                      // turned on, so toggling it on/off here never gets stuck.
                      ...((detergentUsedInScope || criteriaEnabled.detergent) ? [
                        { key: 'detergent', title: 'Detergent Toxicity',        formula: 'MACO_det = (LD50_det × BW / SF) × MBS / LDD', desc: 'Include LD50-based MACO for cleaning detergent as a fifth criterion.', color: '#fbbf24' },
                      ] : []),
                    ].map(c => (
                      <div
                        key={c.key}
                        onClick={() => { if (canEditSettings) { setCriteriaEnabled(prev => ({ ...prev, [c.key]: !prev[c.key] })); setSettingsDirty(true); } }}
                        style={{
                          border: criteriaEnabled[c.key] ? `2px solid ${c.color}` : '1px solid var(--border-color)',
                          borderRadius: '10px', padding: '16px',
                          cursor: canEditSettings ? 'pointer' : 'default',
                          backgroundColor: criteriaEnabled[c.key] ? `color-mix(in srgb, ${c.color} 10%, var(--card-bg))` : 'var(--card-bg)',
                          transition: 'all 0.2s',
                          opacity: canEditSettings ? 1 : 0.75,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: c.color, flexShrink: 0 }}></div>
                          <strong style={{ fontSize: 'var(--fs-base)', color: 'var(--text-primary)' }}>{c.title}</strong>
                          <span style={{ marginLeft: 'auto', fontSize: 'var(--fs-sm)', padding: '2px 8px', borderRadius: '4px', fontWeight: 600,
                            backgroundColor: criteriaEnabled[c.key] ? 'var(--success-bg, #dcfce7)' : 'var(--surface-light)',
                            color: criteriaEnabled[c.key] ? 'var(--success-text, #166534)' : 'var(--text-secondary)' }}>
                            {criteriaEnabled[c.key] ? 'ON' : 'OFF'}
                          </span>
                        </div>
                        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: '6px' }}>{c.desc}</div>
                        <div style={{ fontSize: 'var(--fs-xs)', fontFamily: 'monospace', backgroundColor: 'var(--surface-light, #f9fafb)', padding: '6px 8px', borderRadius: '4px', color: 'var(--primary-color)' }}>{c.formula}</div>
                      </div>
                    ))}
                  </div>

                  {/* Detergent Parameters */}
                  {criteriaEnabled.detergent && (
                    <div style={{ marginTop: '16px', padding: '14px', border: '2px solid var(--warning-border)', borderRadius: '10px', backgroundColor: 'var(--warning-bg)' }}>
                      <strong style={{ fontSize: 'var(--fs-base)', color: 'var(--warning-text)' }}>Detergent Parameters</strong>
                      {configDetergentName && (
                        <div style={{ marginTop: '8px', padding: '6px 10px', borderRadius: '6px',
                                      background: 'color-mix(in srgb, var(--warning-color) 15%, var(--card-bg))',
                                      border: '1px solid var(--warning-border)', fontSize: 'var(--fs-md)', color: 'var(--text-primary)',
                                      display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <FaFlask style={{ color: 'var(--warning-color)', flexShrink: 0 }} />
                          <span>
                            <strong>Detergent(s) recorded in Cleaning Config:</strong>{' '}
                            <em>{configDetergentName}</em>.{' '}
                            {detergentLd50Source.fromMaster
                              ? <>Using <strong>{detergentLd50Source.value} mg/kg</strong> from {detergentLd50Source.detergentName}'s Detergent Master record{configDetergentName.includes(',') ? ' (the lowest/most conservative LD50 among the detergents in use)' : ''}.</>
                              : <>None of these detergents has an LD50 set in Detergent Master yet — falling back to the global value below. Set it on the detergent itself (MACO → Detergents tab) so this criterion reflects the actual chemical in use.</>}
                          </span>
                        </div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginTop: '10px' }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="form-label" style={{ fontSize: 'var(--fs-sm)' }}>Fallback LD50 (mg/kg){detergentLd50Source.fromMaster ? ' — not in use' : ''}</label>
                          <input type="number" className="form-control" value={detergentLd50} disabled={!canEditSettings} onChange={e => { setDetergentLd50(Math.max(0, toNum(e.target.value, 0))); setSettingsDirty(true); }} placeholder="e.g. 2000" />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="form-label" style={{ fontSize: 'var(--fs-sm)' }}>Body Weight (kg)</label>
                          <input type="number" className="form-control" value={detergentBodyWeight} disabled={!canEditSettings} onChange={e => { setDetergentBodyWeight(Math.max(0.1, toNum(e.target.value, 70))); setSettingsDirty(true); }} />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="form-label" style={{ fontSize: 'var(--fs-sm)' }}>Safety Factor</label>
                          <input type="number" className="form-control" value={detergentSafetyFactor} disabled={!canEditSettings} onChange={e => { setDetergentSafetyFactor(Math.max(1, toNum(e.target.value, 2000))); setSettingsDirty(true); }} />
                        </div>
                      </div>
                      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '8px' }}>
                        ADE = LD50 × BW / SF = {effectiveDetergentLd50 > 0 ? ((effectiveDetergentLd50 * detergentBodyWeight) / detergentSafetyFactor).toFixed(4) : '0.0000'} mg/day
                        {detergentLd50Source.fromMaster && <> (using {effectiveDetergentLd50} mg/kg from Detergent Master, not the fallback above)</>}
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          )}

        </div>
      </div>

      {/* PDF Section Selection Modal */}
      {showPdfModal && (
        <Modal isOpen size="sm" title="Export PDF — Select Sections" onClose={() => setShowPdfModal(false)} resizable={false} maximizable={false} minimizable={false}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '14px' }}>
              <p style={{ margin: 0, fontSize: 'var(--fs-base)', color: 'var(--text-secondary)' }}>Choose which sections to include in the exported report.</p>
              <button
                className="btn btn-outline btn-sm"
                style={{ fontSize: 'var(--fs-md)', padding: '3px 10px' }}
                onClick={() => {
                  const allChecked = Object.values(pdfSections).every(Boolean);
                  const availableKeys = { summary: true, governing: true, hbel: criteriaEnabled.hbel, dose: criteriaEnabled.dose, ppm: criteriaEnabled.ppm, tox: criteriaEnabled.tox, detergent: criteriaEnabled.detergent };
                  const allAvailableChecked = Object.entries(availableKeys).filter(([,v]) => v).every(([k]) => pdfSections[k]);
                  setPdfSections(s => {
                    const next = { ...s };
                    const targetValue = !allAvailableChecked;
                    Object.entries(availableKeys).forEach(([k, available]) => { if (available) next[k] = targetValue; });
                    return next;
                  });
                }}
              >
                {Object.entries({ summary: true, governing: true, hbel: criteriaEnabled.hbel, dose: criteriaEnabled.dose, ppm: criteriaEnabled.ppm, tox: criteriaEnabled.tox, detergent: criteriaEnabled.detergent })
                  .filter(([,v]) => v).every(([k]) => pdfSections[k]) ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { key: 'summary', label: 'Summary (Worst-Case Overview)', always: true },
                { key: 'governing', label: 'Governing MACO Matrix', always: true },
                { key: 'hbel', label: 'Health Base (ADE/PDE) Matrix', enabled: criteriaEnabled.hbel },
                { key: 'dose', label: 'Dose-Based (0.1%) Matrix', enabled: criteriaEnabled.dose },
                { key: 'ppm', label: '10 PPM Matrix', enabled: criteriaEnabled.ppm },
                { key: 'tox', label: 'Toxicological Matrix', enabled: criteriaEnabled.tox },
                { key: 'detergent', label: 'Detergent Toxicity Matrix', enabled: criteriaEnabled.detergent, offNote: detergentUsedInScope ? 'disabled in settings' : 'no detergent used for this line' },
              ].map(({ key, label, always, enabled, offNote }) => {
                const isDisabled = !always && !enabled;
                return (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.4 : 1 }}>
                    <input
                      type="checkbox"
                      checked={pdfSections[key]}
                      disabled={isDisabled}
                      onChange={e => setPdfSections(s => ({ ...s, [key]: e.target.checked }))}
                      style={{ width: '16px', height: '16px', accentColor: 'var(--primary-color)' }}
                    />
                    <span style={{ fontSize: 'var(--fs-lg)', color: 'var(--text-primary)' }}>{label}</span>
                    {isDisabled && <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', marginLeft: 'auto' }}>{offNote || 'disabled in settings'}</span>}
                  </label>
                );
              })}
            </div>
            <ModalFooter>
              <button className="btn btn-outline" onClick={() => setShowPdfModal(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => { setShowPdfModal(false); exportPDFTheme(pdfSections); }}>
                <FaDownload style={{ marginRight: '6px' }} /> Export PDF
              </button>
            </ModalFooter>
        </Modal>
      )}

      <AIResultPanel
        isOpen={ai.isOpen}
        onClose={ai.close}
        title={ai.title}
        loading={ai.loading}
        error={ai.error}
        content={ai.content}
        docType="report"
        exportFileName="maco-ai-report.pdf"
      />

      <ESignModal
        isOpen={showGoverningESign}
        title="Sign Governing MACO"
        message="The server will independently recompute this limit from the approved API and product masters and record it as the signed, current governing MACO for this train."
        onCancel={() => setShowGoverningESign(false)}
        commentValue={governingESignData.comment}
        onCommentChange={(value) => setGoverningESignData((p) => ({ ...p, comment: value }))}
        commentPlaceholder="Reason for signing this as the governing MACO…"
        passwordValue={governingESignData.password}
        onPasswordChange={(value) => setGoverningESignData((p) => ({ ...p, password: value }))}
        passwordRef={governingPasswordRef}
        onConfirm={submitFinalizeGoverning}
        confirmLabel={finalizingGoverning ? 'Signing…' : 'Confirm & Sign'}
        confirmDisabled={finalizingGoverning}
      />
    </div>
  );
};

export default MACO;
